import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Award, Bot, BriefcaseBusiness, CheckCircle2, ClipboardCheck, Clock3, Download, FileText, KeyRound, Mail, PlayCircle, RefreshCw, Save, ShieldCheck, Sparkles, Upload, UserPlus, Users } from 'lucide-react';
import { useAuth } from '@/core/hooks';
import { useProductUX } from '@/app/components';

interface CvScreening {
  createdAt: string;
  fileName?: string;
  experience: string;
  skills: string[];
  gaps: string[];
  availability: string[];
  redFlags: string[];
  recommendation: string;
  confidence: string;
  aiNarrative?: string;
  humanApprovalRequired: boolean;
  decisionOwner: string;
}

interface Candidate {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string;
  pipelineStage: string;
  status: string;
  branchId: string;
  roleAppliedFor: string;
  availability: string;
  experienceSummary: string;
  rightToWorkStatus: string;
  dbsStatus: string;
  referenceStatus: string;
  trainingStatus: string;
  aiScreeningSummary: string;
  cvFileName?: string;
  lastCvScreenedAt?: string;
  cvScreening?: CvScreening | null;
  notes?: string;
  convertedCarerId?: string;
  candidatePortalStatus?: string;
  candidatePortalLogin?: {
    email: string;
    temporaryPassword: string;
    status: string;
    issuedAt?: string;
    expiresAt?: string;
    revokedAt?: string;
    revokedReason?: string;
  } | null;
  applicationSubmittedAt?: string;
  candidateExamOutcome?: string;
  candidateExamScore?: string;
  candidateExamSubmittedAt?: string;
  candidatePortalMessage?: string;
  createdAt?: string;
}

interface CandidateDocument {
  id: string;
  candidateId: string;
  documentType: string;
  status: string;
  expiryDate?: string;
  fileName?: string;
  notes?: string;
}

interface HrTask {
  id: string;
  candidateId: string;
  title: string;
  owner: string;
  dueDate: string;
  status: string;
  priority: string;
}

interface ReferenceCheck {
  id: string;
  candidateId: string;
  refereeName: string;
  refereeEmail: string;
  relationship: string;
  status: string;
  sentAt?: string;
  receivedAt?: string;
  outcome?: string;
}

interface Checklist {
  id: string;
  candidateId: string;
  status: string;
  items: Array<{ label: string; completed: boolean; required: boolean }>;
}

interface CompanyDocument {
  id: string;
  candidateId?: string;
  title: string;
  documentType: string;
  category: string;
  status: string;
  signedAt?: string;
  createdAt?: string;
}

interface ReminderLog {
  id: string;
  candidateId?: string;
  ruleTitle?: string;
  message?: string;
  status: string;
  priority?: string;
  createdAt?: string;
  sentAt?: string;
}

interface AuditEvent {
  id: string;
  at: string;
  actor: string;
  role: string;
  action: string;
  collection: string;
  recordId: string;
  details?: string;
}

interface InterviewQuestion {
  id: string;
  category: string;
  question: string;
  expectedAnswer: string;
  candidateAnswer: string;
  score: number;
  maxScore: number;
  notes: string;
  critical?: boolean;
  keywords?: string[];
  legalArea?: string;
}

interface HrInterview {
  id?: string;
  candidateId: string;
  interviewDate: string;
  interviewer: string;
  score: number;
  maxScore: number;
  passMark: number;
  percentage: number;
  recommendation: string;
  status: string;
  examTemplate: string;
  questions: InterviewQuestion[];
  notes: string;
  candidateDeclaration: string;
  finalRecommendation: string;
}

interface RoleExamTemplate {
  id: string;
  title: string;
  targetRoles: string[];
  durationMinutes: number;
  passMark: number;
  maxWrongAnswersAllowed: number;
  maxCriticalErrorsAllowed: number;
  finalDecisionOwner: string;
  legalBasis: string[];
  rules: string[];
  aiInstructions: string[];
  questions: InterviewQuestion[];
}

const stages = ['Applied', 'Screening', 'Interview', 'Offer', 'DBS', 'References', 'Training', 'Ready to Start', 'Rejected'];
const dbsStatuses = ['NOT_STARTED', 'SUBMITTED', 'AWAITING_RESULT', 'CLEAR', 'CONCERN', 'EXPIRED'];
const referenceStatuses = ['SENT', 'REPLIED', 'APPROVED', 'CONCERN'];
const minimumTraining = ['Safeguarding', 'Medication', 'Moving & handling', 'GDPR', 'Infection control', 'Care planning', 'eMAR'];

const ukRecruitmentLegalAreas = [
  'Right to work must be checked before employment starts.',
  'Adult social care roles involving personal care or regulated activity normally require Enhanced DBS with Adults Barred List checks where eligible.',
  'Recruitment decisions must follow equality, dignity, confidentiality and safe recruitment principles.',
  'Care staff must understand safeguarding, duty of candour, infection prevention, record keeping, confidentiality, medication safety and escalation.',
  'The Care Certificate standards support induction for new adult social care workers and were updated in 2025.',
];

function examQuestion(
  id: string,
  category: string,
  question: string,
  expectedAnswer: string,
  keywords: string[],
  critical = false,
  legalArea = 'Safe recruitment'
): InterviewQuestion {
  return {
    id,
    category,
    question,
    expectedAnswer,
    candidateAnswer: '',
    score: 0,
    maxScore: 5,
    notes: '',
    critical,
    keywords,
    legalArea,
  };
}

const interviewQuestionTemplates: InterviewQuestion[] = [
  {
    id: 'safeguarding-concern',
    category: 'Safeguarding',
    question: 'A client tells you privately that a family member is taking money from them. What do you do?',
    expectedAnswer: 'Listen calmly, do not promise secrecy, record facts, report immediately through safeguarding/escalation procedure and protect the client from further risk.',
    candidateAnswer: '',
    score: 0,
    maxScore: 5,
    notes: '',
  },
  {
    id: 'missed-medication',
    category: 'Medication safety',
    question: 'You arrive and see that a medication dose appears to have been missed earlier. What steps do you take?',
    expectedAnswer: 'Do not administer without instruction, check MAR/eMAR, report to senior/manager, follow medication policy, record accurately and escalate if risk is present.',
    candidateAnswer: '',
    score: 0,
    maxScore: 5,
    notes: '',
  },
  {
    id: 'late-visit',
    category: 'Reliability',
    question: 'You are going to be 20 minutes late to a visit. What should happen before the visit time?',
    expectedAnswer: 'Contact office/coordinator immediately, follow rota procedure, never leave client unsupported, record reason and follow instructions.',
    candidateAnswer: '',
    score: 0,
    maxScore: 5,
    notes: '',
  },
  {
    id: 'dignity-choice',
    category: 'Dignity and choice',
    question: 'How do you support personal care while preserving dignity, privacy and choice?',
    expectedAnswer: 'Ask consent, explain each step, offer choices, cover client appropriately, respect preferences, promote independence and record concerns.',
    candidateAnswer: '',
    score: 0,
    maxScore: 5,
    notes: '',
  },
  {
    id: 'moving-handling',
    category: 'Moving and handling',
    question: 'A client asks you to lift them manually, but the care plan says two carers and a hoist are required. What do you do?',
    expectedAnswer: 'Do not manually lift, follow care plan/risk assessment, call office/senior, use correct equipment only when trained and record the issue.',
    candidateAnswer: '',
    score: 0,
    maxScore: 5,
    notes: '',
  },
  {
    id: 'dementia-distress',
    category: 'Dementia care',
    question: 'A client living with dementia becomes distressed and refuses support. How do you respond?',
    expectedAnswer: 'Stay calm, use reassurance, give time, reduce triggers, offer choices, avoid confrontation, keep safe and report changes.',
    candidateAnswer: '',
    score: 0,
    maxScore: 5,
    notes: '',
  },
  {
    id: 'infection-control',
    category: 'Infection control',
    question: 'What infection prevention steps do you follow during a home care visit?',
    expectedAnswer: 'Hand hygiene, correct PPE, safe waste disposal, clean equipment, follow policy and report symptoms/outbreak risks.',
    candidateAnswer: '',
    score: 0,
    maxScore: 5,
    notes: '',
  },
  {
    id: 'record-keeping',
    category: 'Care notes',
    question: 'What makes a good care note after a visit?',
    expectedAnswer: 'Factual, dated/timed, objective, respectful, includes tasks completed, changes, concerns, medication/food/fluid where relevant and unresolved actions.',
    candidateAnswer: '',
    score: 0,
    maxScore: 5,
    notes: '',
  },
  {
    id: 'emergency',
    category: 'Emergency response',
    question: 'You find a client on the floor and they appear injured. What do you do first and next?',
    expectedAnswer: 'Check immediate safety, call emergency services if needed, do not move if unsafe, contact office/manager, reassure client and record incident.',
    candidateAnswer: '',
    score: 0,
    maxScore: 5,
    notes: '',
  },
  {
    id: 'confidentiality',
    category: 'Confidentiality',
    question: 'A neighbour asks you for details about the client’s health. What do you say or do?',
    expectedAnswer: 'Do not share confidential information, be polite, explain you cannot discuss client details and report any concern through the office.',
    candidateAnswer: '',
    score: 0,
    maxScore: 5,
    notes: '',
  },
];

const roleExamTemplates: RoleExamTemplate[] = [
  {
    id: 'exam-care-assistant',
    title: 'Care Assistant Safe Recruitment Exam',
    targetRoles: ['Care Assistant', 'Night Care Assistant', 'Live-in Carer'],
    durationMinutes: 45,
    passMark: 75,
    maxWrongAnswersAllowed: 3,
    maxCriticalErrorsAllowed: 0,
    finalDecisionOwner: 'Registered Manager or HR Officer',
    legalBasis: [
      'Enhanced DBS / Adults Barred List eligibility must be assessed for personal care duties.',
      'Right to work, references, mandatory training and induction must be complete before lone working.',
      'Questions align with safeguarding, dignity, infection prevention, medication prompts, record keeping and escalation expectations.',
    ],
    rules: [
      'Candidate must answer all critical safeguarding, medication and moving-handling questions.',
      'Any critical answer scored below 3/5 blocks automatic progression and requires manager review.',
      'Maximum 3 non-critical weak answers are allowed before second interview or shadowing is recommended.',
      'AI may score and summarise evidence, but it must not make the final employment decision.',
    ],
    aiInstructions: [
      'Ask one question at a time in plain English.',
      'Probe for evidence, escalation route and record keeping if the answer is incomplete.',
      'Keep a calm professional tone and produce a concise final recommendation.',
    ],
    questions: [
      examQuestion('ca-safeguarding-money', 'Safeguarding', 'A client tells you that a relative is taking money from them. What do you do?', 'Listen calmly, do not promise secrecy, record facts, report immediately to the office/safeguarding lead and follow safeguarding policy.', ['listen', 'record', 'report', 'safeguarding', 'not promise secrecy'], true, 'Safeguarding adults'),
      examQuestion('ca-medication-missed', 'Medication safety', 'You notice a medication dose appears to have been missed. What is the safe response?', 'Do not give an extra dose without instruction, check MAR/eMAR, contact senior/manager, record the issue and follow policy.', ['do not', 'emar', 'manager', 'record', 'policy'], true, 'Medication safety'),
      examQuestion('ca-moving-handling', 'Moving and handling', 'The care plan says two carers and hoist, but the client asks you to lift them manually. What do you do?', 'Do not manually lift, follow care plan and risk assessment, call office/senior, use equipment only if trained.', ['do not lift', 'care plan', 'risk assessment', 'call', 'trained'], true, 'Health and safety'),
      examQuestion('ca-dignity-consent', 'Dignity and consent', 'How do you support personal care while protecting dignity and choice?', 'Ask consent, explain each step, offer choices, protect privacy, encourage independence and record concerns.', ['consent', 'explain', 'choice', 'privacy', 'independence'], false, 'Person-centred care'),
      examQuestion('ca-care-notes', 'Care records', 'What makes a care note legally and professionally useful?', 'Factual, dated, timed, respectful, objective, includes tasks, changes, concerns and actions taken.', ['factual', 'dated', 'timed', 'objective', 'concerns'], false, 'Record keeping'),
      examQuestion('ca-confidentiality', 'Confidentiality', 'A neighbour asks about the client health. What do you say?', 'Do not share confidential information, remain polite, explain you cannot discuss client details and report concerns to the office.', ['do not share', 'confidential', 'polite', 'office'], true, 'UK GDPR confidentiality'),
      examQuestion('ca-infection', 'Infection prevention', 'What infection control steps do you follow during a home visit?', 'Hand hygiene, PPE, safe waste disposal, clean equipment and report symptoms or outbreak risks.', ['hand', 'ppe', 'waste', 'clean', 'report'], false, 'Infection prevention'),
      examQuestion('ca-emergency', 'Emergency response', 'You find a client on the floor and they appear injured. What happens first?', 'Check immediate danger, call 999 if needed, do not move if unsafe, reassure client, contact office and record incident.', ['999', 'do not move', 'reassure', 'office', 'record'], true, 'Emergency escalation'),
    ],
  },
  {
    id: 'exam-senior-carer',
    title: 'Senior Carer Leadership and Medication Exam',
    targetRoles: ['Senior Carer', 'Care Supervisor'],
    durationMinutes: 60,
    passMark: 80,
    maxWrongAnswersAllowed: 2,
    maxCriticalErrorsAllowed: 0,
    finalDecisionOwner: 'Registered Manager',
    legalBasis: [
      'Senior care duties require stronger evidence of escalation, supervision, medication safety and audit-ready recording.',
      'High-risk medication and incidents require clear human approval and documented follow-up.',
    ],
    rules: [
      'No critical medication, safeguarding or supervision question may score below 4/5 for senior progression.',
      'Candidate must explain how they would coach a carer and escalate to management.',
      'AI recommendation cannot activate the candidate without Registered Manager approval.',
    ],
    aiInstructions: [
      'Ask for examples from previous work.',
      'Challenge unsafe assumptions and request exact escalation steps.',
      'Summarise leadership risk, medication risk and training needs separately.',
    ],
    questions: [
      examQuestion('sc-med-round', 'Medication leadership', 'A carer reports a possible medication error after leaving the property. What do you do as senior?', 'Secure facts, check eMAR/MAR, contact manager/clinical route if required, assess risk, inform authorised parties per policy, record and follow up.', ['facts', 'emar', 'manager', 'risk', 'record'], true, 'Medication governance'),
      examQuestion('sc-supervision', 'Staff supervision', 'A new carer repeatedly writes vague care notes. How do you manage this?', 'Give coaching, show examples, check understanding, record supervision, monitor improvement and escalate persistent risk.', ['coach', 'examples', 'supervision', 'monitor', 'escalate'], false, 'Supervision'),
      examQuestion('sc-safeguarding', 'Safeguarding escalation', 'A carer tells you they suspect neglect but asks you not to tell anyone. What is your response?', 'Explain safeguarding cannot be kept secret, record facts, escalate immediately to safeguarding lead/manager and protect the client.', ['cannot keep secret', 'record', 'escalate', 'safeguarding', 'protect'], true, 'Safeguarding adults'),
      examQuestion('sc-capacity', 'Mental capacity', 'A client refuses care that appears important for safety. What do you consider?', 'Respect choice, assess capacity concerns, use least restrictive approach, involve manager/family/health professionals if authorised and record.', ['choice', 'capacity', 'least restrictive', 'manager', 'record'], true, 'Mental capacity awareness'),
      examQuestion('sc-audit', 'Audit trail', 'What evidence should exist after a serious incident?', 'Timeline, facts, people involved, immediate actions, notifications, follow-up, risk assessment review and manager decision.', ['timeline', 'facts', 'actions', 'notifications', 'follow-up'], false, 'Audit and incident management'),
      examQuestion('sc-rota-risk', 'Operational risk', 'A rota gap appears for a high-dependency client. What do you do?', 'Escalate immediately, find trained cover, inform coordinator/manager, prioritise risk and document decisions.', ['escalate', 'trained cover', 'manager', 'risk', 'document'], true, 'Continuity of care'),
    ],
  },
  {
    id: 'exam-registered-manager',
    title: 'Registered Manager Governance Exam',
    targetRoles: ['Registered Manager', 'Deputy Manager'],
    durationMinutes: 75,
    passMark: 85,
    maxWrongAnswersAllowed: 1,
    maxCriticalErrorsAllowed: 0,
    finalDecisionOwner: 'Provider / Nominated Individual',
    legalBasis: [
      'Leadership questions cover CQC governance, safe care, staffing, complaints, incidents, safeguarding and data protection accountability.',
      'Evidence must support audit, PIR-style readiness and continuous improvement.',
    ],
    rules: [
      'All governance-critical questions must score at least 4/5.',
      'Candidate must demonstrate evidence-based decision making.',
      'AI can draft a governance risk summary but cannot approve appointment.',
    ],
    aiInstructions: [
      'Ask for concrete examples of governance, inspection readiness and staff accountability.',
      'Flag gaps in CQC evidence, safeguarding oversight and data governance.',
    ],
    questions: [
      examQuestion('rm-cqc-governance', 'CQC governance', 'How would you prove the service is safe, effective, caring, responsive and well-led?', 'Use audits, incidents, complaints, care plan reviews, training matrix, supervision, feedback, action plans and evidence library.', ['audit', 'incidents', 'training', 'feedback', 'action plan'], true, 'CQC governance'),
      examQuestion('rm-safeguarding-lead', 'Safeguarding leadership', 'A safeguarding allegation is made against a staff member. What happens?', 'Protect client, remove immediate risk, follow safeguarding policy, notify appropriate bodies, preserve evidence, record, investigate and support all parties.', ['protect', 'policy', 'notify', 'evidence', 'investigate'], true, 'Safeguarding governance'),
      examQuestion('rm-dpia', 'Data protection', 'What controls are required for sensitive care records and documents?', 'Role access, lawful basis, minimisation, encryption, audit logs, retention, DPIA/ROPA and breach process.', ['role access', 'lawful basis', 'encryption', 'audit', 'DPIA'], true, 'UK GDPR'),
      examQuestion('rm-staffing', 'Safe staffing', 'How do you know rota capacity is safe?', 'Review client needs, skills, travel time, continuity, training, sickness, missed visits, overtime and escalation reports.', ['needs', 'skills', 'travel', 'training', 'missed visits'], false, 'Safe staffing'),
      examQuestion('rm-complaints', 'Complaints', 'How should a formal complaint be managed?', 'Acknowledge, log, investigate, respond within policy timescales, learn lessons, evidence actions and update risk/governance reports.', ['acknowledge', 'log', 'investigate', 'respond', 'lessons'], false, 'Complaints governance'),
    ],
  },
  {
    id: 'exam-care-coordinator',
    title: 'Care Coordinator Scheduling and Risk Exam',
    targetRoles: ['Care Coordinator'],
    durationMinutes: 55,
    passMark: 78,
    maxWrongAnswersAllowed: 2,
    maxCriticalErrorsAllowed: 0,
    finalDecisionOwner: 'Registered Manager',
    legalBasis: [
      'Coordinator decisions affect safe staffing, continuity, travel time, missed visits and client risk.',
      'Questions test rota controls, escalation and communication.',
    ],
    rules: [
      'Rota gaps for high-risk clients must be escalated immediately.',
      'Candidate must show use of skills, availability, location and training before assigning work.',
    ],
    aiInstructions: [
      'Use rota scenarios and ask for prioritisation.',
      'Highlight cost-saving only where safety is not compromised.',
    ],
    questions: [
      examQuestion('cc-rota-gap', 'Rota safety', 'A carer calls sick 30 minutes before a double-up visit. What do you do?', 'Assess risk, find trained replacement, contact senior/manager, update client/family if appropriate, document and monitor.', ['risk', 'trained', 'manager', 'update', 'document'], true, 'Safe staffing'),
      examQuestion('cc-skill-match', 'Skill matching', 'What must be checked before assigning a carer to a client?', 'Training, competence, DBS/right to work status, availability, distance, continuity and client preferences.', ['training', 'competence', 'availability', 'distance', 'preferences'], false, 'Safe scheduling'),
      examQuestion('cc-late-checkin', 'Late check-in', 'A carer has not checked in 10 minutes after start time. What happens?', 'Contact carer, check GPS/telemetry, alert senior, protect client continuity and record the action.', ['contact', 'GPS', 'senior', 'client', 'record'], true, 'Visit monitoring'),
      examQuestion('cc-cost-control', 'Cost control', 'How can rota cost be reduced without risking care quality?', 'Reduce unnecessary travel, avoid overtime, match nearby trained carers, plan continuity and review underused capacity.', ['travel', 'overtime', 'nearby', 'continuity', 'capacity'], false, 'Operational efficiency'),
      examQuestion('cc-confidentiality', 'Confidentiality', 'How do you handle family requests for internal staffing details?', 'Share only permitted information, avoid internal staff details unless authorised, record the request and escalate sensitive issues.', ['permitted', 'authorised', 'record', 'escalate'], false, 'Confidentiality'),
    ],
  },
  {
    id: 'exam-hr-officer',
    title: 'Recruitment and HR Officer Safe Recruitment Exam',
    targetRoles: ['Recruitment and HR Officer', 'Recruitment Officer', 'HR Officer'],
    durationMinutes: 60,
    passMark: 80,
    maxWrongAnswersAllowed: 2,
    maxCriticalErrorsAllowed: 0,
    finalDecisionOwner: 'Registered Manager',
    legalBasis: [
      'Safe recruitment requires right-to-work checks, references, role-appropriate DBS checks, fair selection and documented decisions.',
      'AI may assist screening but must not make protected-characteristic or final hiring decisions.',
    ],
    rules: [
      'No candidate may be activated until mandatory checks are complete.',
      'AI red flags must be reviewed by a human and recorded objectively.',
    ],
    aiInstructions: [
      'Check for missing evidence and bias risk.',
      'Use neutral language and avoid protected-characteristic assumptions.',
    ],
    questions: [
      examQuestion('hr-rtw', 'Right to work', 'When must right-to-work evidence be checked and recorded?', 'Before employment starts, using valid original/digital evidence according to current process, with record retained.', ['before', 'valid', 'evidence', 'record', 'retained'], true, 'Right to work'),
      examQuestion('hr-dbs', 'DBS eligibility', 'How do you decide the correct DBS level for a care role?', 'Assess duties against regulated activity/eligibility guidance, document rationale and request appropriate enhanced/barred list check where eligible.', ['duties', 'regulated activity', 'eligibility', 'document', 'enhanced'], true, 'DBS safe recruitment'),
      examQuestion('hr-references', 'References', 'What makes references recruitment-ready?', 'Two satisfactory references where possible, identity of referee verified, concerns escalated and responses stored in candidate file.', ['two', 'verified', 'concerns', 'stored'], false, 'Safe recruitment'),
      examQuestion('hr-ai-bias', 'AI and fairness', 'How should AI CV screening be used safely?', 'As a draft only, avoid bias, ignore protected characteristics, require human review and record final human reasons.', ['draft', 'bias', 'protected', 'human review', 'reasons'], true, 'Equality and AI governance'),
      examQuestion('hr-records', 'HR records', 'What must be stored before converting a candidate to carer?', 'Application, interview, right-to-work, DBS status, references, training, contract and manager approval.', ['application', 'interview', 'right-to-work', 'DBS', 'approval'], false, 'HR audit trail'),
    ],
  },
  {
    id: 'exam-accountant',
    title: 'Accountant Payroll and Billing Control Exam',
    targetRoles: ['Accountant', 'Contabil'],
    durationMinutes: 45,
    passMark: 75,
    maxWrongAnswersAllowed: 2,
    maxCriticalErrorsAllowed: 0,
    finalDecisionOwner: 'Company Director or Registered Manager',
    legalBasis: [
      'Finance roles must protect payroll, billing, sensitive staff/client data and audit evidence.',
      'Payroll and invoices should be generated from verified visits and approved rates.',
    ],
    rules: [
      'Candidate must identify payroll and invoice errors before approval.',
      'Sensitive financial and personal data must remain confidential.',
    ],
    aiInstructions: [
      'Ask calculation and control questions.',
      'Flag confidentiality or approval-control gaps.',
    ],
    questions: [
      examQuestion('acc-payroll-source', 'Payroll control', 'What source should payroll use for carer hours?', 'Verified check-in/check-out records, approved rota exceptions and authorised overtime rules.', ['verified', 'check-in', 'check-out', 'approved', 'overtime'], true, 'Payroll controls'),
      examQuestion('acc-billing-source', 'Billing control', 'How should client invoices be generated?', 'From completed verified visits, billable rates, adjustments and payment status with audit trail.', ['completed', 'verified', 'rates', 'adjustments', 'audit'], false, 'Billing controls'),
      examQuestion('acc-confidentiality', 'Confidentiality', 'Can payroll details be shared with another employee?', 'No, only authorised roles may access payroll data and every access should be logged.', ['no', 'authorised', 'access', 'logged'], true, 'UK GDPR confidentiality'),
      examQuestion('acc-error', 'Error handling', 'A carer says their payslip is wrong. What do you check?', 'Timesheets, verified visits, rate, overtime rules, mileage, deductions, approvals and audit log.', ['timesheets', 'rate', 'overtime', 'mileage', 'audit'], false, 'Financial audit'),
    ],
  },
  {
    id: 'exam-technical-admin',
    title: 'Technical Admin Security and Integrations Exam',
    targetRoles: ['Technical Admin', 'Tehnic Admin'],
    durationMinutes: 50,
    passMark: 80,
    maxWrongAnswersAllowed: 2,
    maxCriticalErrorsAllowed: 0,
    finalDecisionOwner: 'System Owner',
    legalBasis: [
      'Technical roles can affect security, API keys, backups, audit logs and sensitive personal data.',
      'Access must follow least privilege and server-side secret storage.',
    ],
    rules: [
      'Any weak answer on API key exposure, backups or access control blocks automatic progression.',
      'Candidate must understand not to expose real secrets in browser code.',
    ],
    aiInstructions: [
      'Probe for practical operational security.',
      'Require examples of incident response and least privilege.',
    ],
    questions: [
      examQuestion('ta-api-keys', 'API key security', 'Where should real API keys be stored and why?', 'Server-side encrypted/configured storage, never exposed in browser, with access limited and audited.', ['server', 'encrypted', 'never browser', 'limited', 'audited'], true, 'Security controls'),
      examQuestion('ta-backup', 'Backup and restore', 'How should backups be managed for sensitive care data?', 'Encrypted backups, tested restore process, restricted access, retention policy and audit logs.', ['encrypted', 'restore', 'restricted', 'retention', 'audit'], true, 'Data security'),
      examQuestion('ta-access', 'Role access', 'How do you apply least privilege?', 'Give only required permissions, review access, remove unused rights and log sensitive actions.', ['required', 'review', 'remove', 'log'], false, 'Access control'),
      examQuestion('ta-incident', 'Security incident', 'What do you do after suspected data exposure?', 'Contain, preserve logs, notify responsible lead, assess breach, document actions and follow incident response policy.', ['contain', 'logs', 'notify', 'breach', 'document'], true, 'Incident response'),
    ],
  },
  {
    id: 'exam-driver',
    title: 'Company Driver and Community Transport Exam',
    targetRoles: ['Driver', 'Carer Driver'],
    durationMinutes: 35,
    passMark: 72,
    maxWrongAnswersAllowed: 2,
    maxCriticalErrorsAllowed: 0,
    finalDecisionOwner: 'Registered Manager or Fleet Owner',
    legalBasis: [
      'Drivers supporting adults receiving care may require role-appropriate DBS eligibility assessment depending on duties.',
      'Fleet users must follow safety, insurance, mileage and confidentiality controls.',
    ],
    rules: [
      'Candidate must pass safety, confidentiality and incident reporting questions.',
      'Vehicle use remains subject to licence, insurance and company policy checks.',
    ],
    aiInstructions: [
      'Ask practical driving, client safety and reporting scenarios.',
      'Flag unsafe manual handling or confidentiality answers.',
    ],
    questions: [
      examQuestion('dr-vehicle-check', 'Vehicle safety', 'What checks should be completed before using a company vehicle?', 'Licence/insurance permission, vehicle condition, fuel, tyres, lights, mileage and defect reporting.', ['licence', 'insurance', 'tyres', 'lights', 'defect'], true, 'Fleet safety'),
      examQuestion('dr-client-info', 'Confidentiality', 'Can you discuss a client route or health details with another person?', 'No. Client information is confidential and only shared with authorised staff for care reasons.', ['no', 'confidential', 'authorised', 'care'], true, 'Confidentiality'),
      examQuestion('dr-incident', 'Incident reporting', 'What do you do after a vehicle incident while on duty?', 'Ensure safety, call emergency services if needed, inform office/manager, record details, do not admit liability outside policy.', ['safety', 'emergency', 'office', 'record', 'policy'], true, 'Incident management'),
      examQuestion('dr-costs', 'Cost records', 'How should fuel or mileage costs be recorded?', 'Record date, vehicle, mileage, litres, amount, receipt and reason for journey.', ['date', 'vehicle', 'mileage', 'litres', 'receipt'], false, 'Fleet cost control'),
    ],
  },
];

const demoHrDate = (daysFromToday: number) => new Date(Date.now() + daysFromToday * 86400000).toISOString().slice(0, 10);
const demoHrTimestamp = (daysFromToday: number) => new Date(Date.now() + daysFromToday * 86400000).toISOString();

function buildDemoInterviewQuestions(candidateName: string, level: 'strong' | 'good' | 'needs-training' | 'concern' = 'good'): InterviewQuestion[] {
  const answerMap: Record<string, string> = {
    'safeguarding-concern': 'I would listen calmly, avoid promising secrecy, write factual notes and report immediately to the office or safeguarding lead so the client is protected.',
    'missed-medication': 'I would not give an extra dose. I would check the eMAR, call the senior or manager, record the issue and follow the medication policy.',
    'late-visit': 'I would call the office before the visit time, explain the reason, follow coordinator instructions and make sure the client is not left unsupported.',
    'dignity-choice': 'I would ask consent, explain what I am doing, offer choices, keep the client covered and encourage independence where safe.',
    'moving-handling': 'I would follow the care plan and risk assessment. If two carers and a hoist are required, I would not lift manually and would call the office.',
    'dementia-distress': 'I would stay calm, reduce pressure, offer reassurance and choices, give the person time and report any unusual change in behaviour.',
    'infection-control': 'I would wash hands, use PPE correctly, dispose of waste safely, clean equipment and report infection symptoms.',
    'record-keeping': 'My notes would be factual, respectful, dated and include tasks completed, changes, concerns and any actions required.',
    emergency: 'I would check immediate safety, call 999 if needed, not move the client if unsafe, reassure them and contact the office.',
    confidentiality: 'I would not share private information with a neighbour and would politely explain that client details are confidential.',
  };
  const baseScores = {
    strong: [5, 5, 5, 5, 5, 4, 5, 5, 5, 5],
    good: [4, 4, 5, 4, 4, 4, 4, 5, 4, 4],
    'needs-training': [4, 3, 4, 4, 3, 4, 4, 3, 4, 4],
    concern: [3, 2, 3, 4, 2, 3, 3, 3, 3, 4],
  }[level];

  return interviewQuestionTemplates.map((question, index) => ({
    ...question,
    candidateAnswer: `${candidateName}: ${answerMap[question.id] || question.expectedAnswer}`,
    score: baseScores[index] || 3,
    notes: level === 'strong'
      ? 'Strong answer with clear escalation, records and person-centred language.'
      : level === 'good'
        ? 'Good practical answer. Minor prompting may be needed during induction.'
        : level === 'needs-training'
          ? 'Acceptable foundation, but induction should reinforce policy, recording and escalation.'
          : 'Requires manager review and targeted training before any offer decision.',
  }));
}

function completeDemoInterview(candidateId: string, candidateName: string, interviewer: string, level: 'strong' | 'good' | 'needs-training' | 'concern', daysAgo: number): HrInterview {
  const questions = buildDemoInterviewQuestions(candidateName, level);
  const score = questions.reduce((sum, question) => sum + question.score, 0);
  const maxScore = questions.reduce((sum, question) => sum + question.maxScore, 0);
  const percentage = Math.round((score / maxScore) * 100);
  const recommendation = percentage >= 80 ? 'PROGRESS_TO_CONDITIONAL_OFFER' : percentage >= 65 ? 'PROGRESS_WITH_TRAINING_PLAN' : percentage >= 50 ? 'SECOND_INTERVIEW_OR_SHADOWING' : 'DO_NOT_PROGRESS';
  return {
    id: `demo-interview-${candidateId}`,
    candidateId,
    interviewDate: demoHrDate(-daysAgo),
    interviewer,
    score,
    maxScore,
    passMark: 70,
    percentage,
    recommendation,
    status: 'COMPLETED_PENDING_HUMAN_DECISION',
    examTemplate: level === 'strong' ? 'Senior Care Worker Employment Exam' : 'Care Assistant Employment Exam',
    questions,
    notes: `${candidateName} completed a structured care-sector interview exam. Result: ${percentage}%. Human HR/manager decision is still required before employment.`,
    candidateDeclaration: 'I confirm these answers are my own and true to the best of my knowledge.',
    finalRecommendation: `Exam score ${score}/${maxScore} (${percentage}%). Automated recommendation: ${recommendation}. Check DBS, references, right to work and training before any final decision.`,
  };
}

function trainingItems(completedLabels: string[]) {
  return minimumTraining.map((label) => ({
    label,
    completed: completedLabels.includes(label),
    required: true,
  }));
}

function createDemoRecruitmentDataset() {
  const candidates: Candidate[] = [
    {
      id: 'demo-candidate-amelia-carter',
      firstName: 'Amelia',
      lastName: 'Carter',
      fullName: 'Amelia Carter',
      email: 'amelia.carter.demo@fuchsiahr.co.uk',
      phone: '07700 900101',
      pipelineStage: 'Screening',
      status: 'ACTIVE',
      branchId: 'gorleston',
      roleAppliedFor: 'Care Assistant',
      availability: 'Monday to Friday 07:00-14:00, alternate weekends',
      experienceSummary: 'Three years in domiciliary care, dementia support, personal care and medication prompts.',
      rightToWorkStatus: 'VERIFIED',
      dbsStatus: 'SUBMITTED',
      referenceStatus: 'SENT',
      trainingStatus: 'NOT_STARTED',
      aiScreeningSummary: 'Strong CV match for domiciliary care. HR should verify gaps in 2024 employment, second reference and DBS result before offer.',
      cvFileName: 'Amelia_Carter_Care_Assistant_CV.pdf',
      lastCvScreenedAt: demoHrTimestamp(-2),
      createdAt: demoHrTimestamp(-11),
      notes: 'Available immediately. Prefers Gorleston and Great Yarmouth rounds.',
      cvScreening: {
        createdAt: demoHrTimestamp(-2),
        fileName: 'Amelia_Carter_Care_Assistant_CV.pdf',
        experience: '3 years domiciliary care, dementia support, personal care, meal preparation, medication prompts and care note recording.',
        skills: ['Personal care', 'Dementia care', 'Medication prompts', 'Care notes', 'Safeguarding awareness', 'Driving licence'],
        gaps: ['Verify one 4-month employment gap in 2024', 'Confirm weekend availability in writing'],
        availability: ['Weekday mornings', 'Alternate weekends', 'Can start after DBS result'],
        redFlags: ['No high-risk red flag detected', 'Needs DBS result before lone working'],
        recommendation: 'Progress to interview after second reference request is sent.',
        confidence: 'High',
        aiNarrative: 'Candidate appears suitable for standard domiciliary care pending checks.',
        humanApprovalRequired: true,
        decisionOwner: 'Recruitment and HR Officer',
      },
    },
    {
      id: 'demo-candidate-daniel-moore',
      firstName: 'Daniel',
      lastName: 'Moore',
      fullName: 'Daniel Moore',
      email: 'daniel.moore.demo@fuchsiahr.co.uk',
      phone: '07700 900102',
      pipelineStage: 'Interview',
      status: 'ACTIVE',
      branchId: 'gorleston',
      roleAppliedFor: 'Senior Carer',
      availability: 'Full time, evenings, weekends and on-call support',
      experienceSummary: 'Seven years care experience, shift lead, medication rounds, mentoring new carers and incident escalation.',
      rightToWorkStatus: 'VERIFIED',
      dbsStatus: 'CLEAR',
      referenceStatus: 'APPROVED',
      trainingStatus: 'IN_PROGRESS',
      aiScreeningSummary: 'Very strong senior carer candidate. Suitable for supervisor pathway after policy induction and local rota training.',
      cvFileName: 'Daniel_Moore_Senior_Carer_CV.docx',
      lastCvScreenedAt: demoHrTimestamp(-6),
      createdAt: demoHrTimestamp(-19),
      notes: 'Interview passed. Complete medication refresher and contract signature.',
      cvScreening: {
        createdAt: demoHrTimestamp(-6),
        fileName: 'Daniel_Moore_Senior_Carer_CV.docx',
        experience: '7 years care work including senior duties, medication rounds, staff shadowing, emergency response and family communication.',
        skills: ['Senior carer leadership', 'Medication administration', 'Rota support', 'Incident reporting', 'Mentoring', 'eMAR experience'],
        gaps: ['Confirm references cover last 5 years', 'Check competence evidence for high-risk medication'],
        availability: ['Full-time', 'Evenings', 'Weekends', 'On-call support'],
        redFlags: ['No red flags detected from CV text'],
        recommendation: 'Progress to conditional offer with manager approval.',
        confidence: 'Very high',
        aiNarrative: 'Strong candidate for senior care duties with clear operational experience.',
        humanApprovalRequired: true,
        decisionOwner: 'Registered Manager',
      },
    },
    {
      id: 'demo-candidate-sofia-patel',
      firstName: 'Sofia',
      lastName: 'Patel',
      fullName: 'Sofia Patel',
      email: 'sofia.patel.demo@fuchsiahr.co.uk',
      phone: '07700 900103',
      pipelineStage: 'DBS',
      status: 'ACTIVE',
      branchId: 'lowestoft',
      roleAppliedFor: 'Care Assistant',
      availability: 'Part time, school hours, no nights',
      experienceSummary: 'Hospitality background, volunteer befriending, strong communication, new to regulated care.',
      rightToWorkStatus: 'PENDING',
      dbsStatus: 'AWAITING_RESULT',
      referenceStatus: 'REPLIED',
      trainingStatus: 'NOT_STARTED',
      aiScreeningSummary: 'Promising entry-level candidate. Needs right-to-work verification, DBS completion and full induction before client-facing work.',
      cvFileName: 'Sofia_Patel_Entry_Level_Care_CV.txt',
      lastCvScreenedAt: demoHrTimestamp(-3),
      createdAt: demoHrTimestamp(-8),
      notes: 'Potential for companionship calls after training.',
      cvScreening: {
        createdAt: demoHrTimestamp(-3),
        fileName: 'Sofia_Patel_Entry_Level_Care_CV.txt',
        experience: 'No paid care experience. Volunteer befriending and customer-facing hospitality work with transferable communication skills.',
        skills: ['Communication', 'Reliability', 'Companionship', 'Food hygiene', 'Basic record keeping'],
        gaps: ['No formal care experience', 'Needs full mandatory training', 'Right to work document must be checked'],
        availability: ['Weekdays 09:30-14:30', 'No nights', 'No Sundays'],
        redFlags: ['No paid care history means close shadowing required'],
        recommendation: 'Progress only with full induction, shadowing and manager-approved first visits.',
        confidence: 'Medium',
        aiNarrative: 'Potential hire for companionship/personal care pathway after training.',
        humanApprovalRequired: true,
        decisionOwner: 'Deputy Manager',
      },
    },
    {
      id: 'demo-candidate-oliver-hughes',
      firstName: 'Oliver',
      lastName: 'Hughes',
      fullName: 'Oliver Hughes',
      email: 'oliver.hughes.demo@fuchsiahr.co.uk',
      phone: '07700 900104',
      pipelineStage: 'Training',
      status: 'ACTIVE',
      branchId: 'gorleston',
      roleAppliedFor: 'Care Assistant',
      availability: 'Weekends, evenings and emergency cover',
      experienceSummary: 'Former support worker with learning disability experience, positive behaviour support and community access.',
      rightToWorkStatus: 'VERIFIED',
      dbsStatus: 'CLEAR',
      referenceStatus: 'APPROVED',
      trainingStatus: 'IN_PROGRESS',
      aiScreeningSummary: 'Good candidate for complex support after moving and handling refresh, eMAR training and shadow shifts.',
      cvFileName: 'Oliver_Hughes_Support_Worker_CV.pdf',
      lastCvScreenedAt: demoHrTimestamp(-9),
      createdAt: demoHrTimestamp(-23),
      notes: 'Training almost complete. Arrange two shadow visits.',
      cvScreening: {
        createdAt: demoHrTimestamp(-9),
        fileName: 'Oliver_Hughes_Support_Worker_CV.pdf',
        experience: '4 years support work including learning disability support, community access, behaviour plans and incident recording.',
        skills: ['Learning disability support', 'Behaviour support', 'Incident records', 'Community access', 'Team communication'],
        gaps: ['Medication experience limited', 'Needs eMAR practice before medication rounds'],
        availability: ['Evenings', 'Weekends', 'Emergency cover'],
        redFlags: ['Medication training gap for eMAR use'],
        recommendation: 'Continue onboarding and assign shadow visits.',
        confidence: 'High',
        aiNarrative: 'Suitable candidate with defined training needs.',
        humanApprovalRequired: true,
        decisionOwner: 'Care Supervisor',
      },
    },
    {
      id: 'demo-candidate-grace-morgan',
      firstName: 'Grace',
      lastName: 'Morgan',
      fullName: 'Grace Morgan',
      email: 'grace.morgan.demo@fuchsiahr.co.uk',
      phone: '07700 900105',
      pipelineStage: 'Ready to Start',
      status: 'READY',
      branchId: 'gorleston',
      roleAppliedFor: 'Night Care Assistant',
      availability: 'Nights, 20:00-08:00, four shifts per week',
      experienceSummary: 'Five years night care, end-of-life support, medication support and fall response.',
      rightToWorkStatus: 'VERIFIED',
      dbsStatus: 'CLEAR',
      referenceStatus: 'APPROVED',
      trainingStatus: 'COMPLETE',
      aiScreeningSummary: 'Ready to start. Checks complete, signed contract stored and mandatory training complete.',
      cvFileName: 'Grace_Morgan_Night_Care_CV.pdf',
      lastCvScreenedAt: demoHrTimestamp(-14),
      convertedCarerId: 'demo-carer-grace-morgan',
      createdAt: demoHrTimestamp(-31),
      notes: 'Converted to carer profile for night care rota testing.',
      cvScreening: {
        createdAt: demoHrTimestamp(-14),
        fileName: 'Grace_Morgan_Night_Care_CV.pdf',
        experience: '5 years night care, end-of-life support, falls response, medication support and family updates.',
        skills: ['Night care', 'End-of-life care', 'Medication support', 'Falls response', 'Family communication', 'Care notes'],
        gaps: ['No current gaps detected'],
        availability: ['Night shifts', 'Four shifts per week', 'Can cover urgent nights'],
        redFlags: ['No red flags detected'],
        recommendation: 'Ready to start and suitable for night care rota.',
        confidence: 'Very high',
        aiNarrative: 'Checks and training complete. Candidate can be used to test carer conversion and rota workflows.',
        humanApprovalRequired: true,
        decisionOwner: 'Registered Manager',
      },
    },
  ];

  const documents: CandidateDocument[] = candidates.flatMap((candidate, index) => {
    const isReady = candidate.pipelineStage === 'Ready to Start';
    const dbsExpiry = candidate.dbsStatus === 'CLEAR' ? demoHrDate(330 - index * 17) : undefined;
    return [
      { id: `${candidate.id}-passport`, candidateId: candidate.id, documentType: 'Passport / Photo ID', status: candidate.rightToWorkStatus === 'VERIFIED' ? 'VERIFIED' : 'PENDING', notes: 'Photo ID copied and checked against candidate record.' },
      { id: `${candidate.id}-right-to-work`, candidateId: candidate.id, documentType: 'Right to Work', status: candidate.rightToWorkStatus === 'VERIFIED' ? 'VERIFIED' : 'PENDING', expiryDate: candidate.rightToWorkStatus === 'VERIFIED' ? demoHrDate(540) : undefined, notes: candidate.rightToWorkStatus === 'VERIFIED' ? 'Right to work check recorded.' : 'Awaiting original document check.' },
      { id: `${candidate.id}-dbs`, candidateId: candidate.id, documentType: 'DBS Certificate', status: candidate.dbsStatus === 'CLEAR' ? 'VERIFIED' : candidate.dbsStatus, expiryDate: dbsExpiry, notes: candidate.dbsStatus === 'CLEAR' ? 'Enhanced DBS clear.' : 'DBS application in progress.' },
      { id: `${candidate.id}-references`, candidateId: candidate.id, documentType: 'References', status: candidate.referenceStatus === 'APPROVED' ? 'VERIFIED' : candidate.referenceStatus, notes: candidate.referenceStatus === 'APPROVED' ? 'Two satisfactory references stored.' : 'Reference chase required.' },
      { id: `${candidate.id}-cv`, candidateId: candidate.id, documentType: 'CV', status: 'VERIFIED', notes: `${candidate.cvFileName} screened and attached to candidate file.` },
      { id: `${candidate.id}-contract`, candidateId: candidate.id, documentType: 'Employment Contract', status: isReady ? 'SIGNED' : 'DRAFT', notes: isReady ? 'Signed digitally and stored in HR documents.' : 'Contract template prepared for HR review.' },
    ];
  });

  const companyDocuments: CompanyDocument[] = [
    { id: 'demo-template-care-assistant-exam', title: 'Care Assistant Employment Exam Template', documentType: 'Exam Template', category: 'Recruitment & HR', status: 'ACTIVE', createdAt: demoHrTimestamp(-30) },
    { id: 'demo-template-senior-carer-exam', title: 'Senior Carer Employment Exam Template', documentType: 'Exam Template', category: 'Recruitment & HR', status: 'ACTIVE', createdAt: demoHrTimestamp(-30) },
    { id: 'demo-template-reference-request', title: 'Reference Request Email Template', documentType: 'Email Template', category: 'Recruitment & HR', status: 'ACTIVE', createdAt: demoHrTimestamp(-25) },
    { id: 'demo-template-employment-contract', title: 'Care Worker Employment Contract Template', documentType: 'Employment Contract Template', category: 'Recruitment & HR', status: 'ACTIVE', createdAt: demoHrTimestamp(-25) },
    ...candidates.map((candidate) => ({
      id: `demo-contract-${candidate.id}`,
      candidateId: candidate.id,
      title: `${candidate.fullName} - Employment Contract`,
      documentType: 'Employment Contract',
      category: 'Recruitment & HR',
      status: candidate.pipelineStage === 'Ready to Start' ? 'SIGNED' : candidate.pipelineStage === 'Training' ? 'READY_TO_SIGN' : 'DRAFT',
      signedAt: candidate.pipelineStage === 'Ready to Start' ? demoHrTimestamp(-3) : undefined,
      createdAt: demoHrTimestamp(-6),
    })),
  ];

  const references: ReferenceCheck[] = candidates.flatMap((candidate, index) => [
    {
      id: `${candidate.id}-reference-1`,
      candidateId: candidate.id,
      refereeName: ['Helen Ward', 'Marcus Lee', 'Aisha Khan', 'Robert Finch', 'Emily Davies'][index],
      refereeEmail: `reference${index + 1}a.demo@example.com`,
      relationship: 'Previous manager',
      status: candidate.referenceStatus === 'APPROVED' ? 'APPROVED' : candidate.referenceStatus === 'REPLIED' ? 'REPLIED' : 'SENT',
      sentAt: demoHrTimestamp(-9 + index),
      receivedAt: ['APPROVED', 'REPLIED'].includes(candidate.referenceStatus) ? demoHrTimestamp(-5 + index) : undefined,
      outcome: candidate.referenceStatus === 'APPROVED'
        ? 'Confirmed reliable attendance, good care notes, respectful communication and no safeguarding concerns.'
        : candidate.referenceStatus === 'REPLIED'
          ? 'Reference received. HR to review detail before approval.'
          : 'Request sent. Awaiting response.',
    },
    {
      id: `${candidate.id}-reference-2`,
      candidateId: candidate.id,
      refereeName: ['Olivia Green', 'Caroline Smith', 'Nina Brooks', 'Paula Evans', 'George Martin'][index],
      refereeEmail: `reference${index + 1}b.demo@example.com`,
      relationship: index === 2 ? 'Volunteer coordinator' : 'Care coordinator',
      status: candidate.referenceStatus === 'APPROVED' ? 'APPROVED' : 'SENT',
      sentAt: demoHrTimestamp(-8 + index),
      receivedAt: candidate.referenceStatus === 'APPROVED' ? demoHrTimestamp(-4 + index) : undefined,
      outcome: candidate.referenceStatus === 'APPROVED' ? 'Second reference satisfactory and approved.' : 'Second reference pending. Reminder should be sent automatically.',
    },
  ]);

  const interviews: HrInterview[] = [
    completeDemoInterview('demo-candidate-amelia-carter', 'Amelia Carter', 'Recruitment and HR Officer', 'good', 3),
    completeDemoInterview('demo-candidate-daniel-moore', 'Daniel Moore', 'Registered Manager', 'strong', 7),
    completeDemoInterview('demo-candidate-sofia-patel', 'Sofia Patel', 'Deputy Manager', 'needs-training', 2),
    completeDemoInterview('demo-candidate-oliver-hughes', 'Oliver Hughes', 'Care Supervisor', 'good', 10),
    completeDemoInterview('demo-candidate-grace-morgan', 'Grace Morgan', 'Registered Manager', 'strong', 16),
  ];

  const tasks: HrTask[] = candidates.flatMap((candidate, index) => [
    { id: `${candidate.id}-task-rtw`, candidateId: candidate.id, title: 'Verify right-to-work evidence', owner: 'Recruitment and HR Officer', dueDate: demoHrDate(index - 1), status: candidate.rightToWorkStatus === 'VERIFIED' ? 'COMPLETE' : 'PENDING', priority: candidate.rightToWorkStatus === 'VERIFIED' ? 'LOW' : 'HIGH' },
    { id: `${candidate.id}-task-dbs`, candidateId: candidate.id, title: 'Complete DBS tracker and expiry date', owner: 'Deputy Manager', dueDate: demoHrDate(index + 1), status: candidate.dbsStatus === 'CLEAR' ? 'COMPLETE' : 'IN_PROGRESS', priority: candidate.dbsStatus === 'CLEAR' ? 'LOW' : 'HIGH' },
    { id: `${candidate.id}-task-training`, candidateId: candidate.id, title: 'Complete mandatory onboarding training', owner: 'Care Supervisor', dueDate: demoHrDate(index + 4), status: candidate.trainingStatus === 'COMPLETE' ? 'COMPLETE' : 'IN_PROGRESS', priority: candidate.trainingStatus === 'COMPLETE' ? 'LOW' : 'MEDIUM' },
  ]);

  const checklists: Checklist[] = [
    { id: 'demo-checklist-amelia', candidateId: 'demo-candidate-amelia-carter', status: 'IN_PROGRESS', items: trainingItems(['GDPR']) },
    { id: 'demo-checklist-daniel', candidateId: 'demo-candidate-daniel-moore', status: 'IN_PROGRESS', items: trainingItems(['Safeguarding', 'Medication', 'Moving & handling', 'GDPR', 'Infection control']) },
    { id: 'demo-checklist-sofia', candidateId: 'demo-candidate-sofia-patel', status: 'NOT_STARTED', items: trainingItems([]) },
    { id: 'demo-checklist-oliver', candidateId: 'demo-candidate-oliver-hughes', status: 'IN_PROGRESS', items: trainingItems(['Safeguarding', 'Moving & handling', 'GDPR', 'Infection control', 'Care planning']) },
    { id: 'demo-checklist-grace', candidateId: 'demo-candidate-grace-morgan', status: 'COMPLETE', items: trainingItems(minimumTraining) },
  ];

  const reminders: ReminderLog[] = [
    { id: 'demo-reminder-amelia-reference', candidateId: 'demo-candidate-amelia-carter', ruleTitle: 'Reference follow-up', message: 'Second reference still pending for Amelia Carter.', status: 'SCHEDULED', priority: 'HIGH', createdAt: demoHrTimestamp(-1), sentAt: demoHrTimestamp(0) },
    { id: 'demo-reminder-sofia-rtw', candidateId: 'demo-candidate-sofia-patel', ruleTitle: 'Right to work required', message: 'Sofia Patel must provide right-to-work evidence before offer.', status: 'SCHEDULED', priority: 'HIGH', createdAt: demoHrTimestamp(-1) },
    { id: 'demo-reminder-daniel-contract', candidateId: 'demo-candidate-daniel-moore', ruleTitle: 'Contract signature', message: 'Daniel Moore contract is ready and needs signature.', status: 'SENT', priority: 'MEDIUM', createdAt: demoHrTimestamp(-2), sentAt: demoHrTimestamp(-1) },
    { id: 'demo-reminder-oliver-training', candidateId: 'demo-candidate-oliver-hughes', ruleTitle: 'Training completion', message: 'Oliver Hughes needs medication and eMAR training before activation.', status: 'SCHEDULED', priority: 'MEDIUM', createdAt: demoHrTimestamp(-1) },
    { id: 'demo-reminder-hr-report', ruleTitle: 'Weekly recruitment report', message: 'Send weekly HR pipeline report to manager.', status: 'SENT', priority: 'LOW', createdAt: demoHrTimestamp(-5), sentAt: demoHrTimestamp(-5) },
  ];

  const auditEvents: AuditEvent[] = [
    { id: 'demo-audit-1', at: demoHrTimestamp(-14), actor: 'Recruitment and HR Officer', role: 'Recruitment and HR Officer', action: 'CV screened by AI draft', collection: 'hrCandidates', recordId: 'demo-candidate-grace-morgan', details: 'Grace Morgan CV screened and marked ready for manager review.' },
    { id: 'demo-audit-2', at: demoHrTimestamp(-10), actor: 'Care Supervisor', role: 'Care Supervisor', action: 'Interview exam saved', collection: 'hrInterviews', recordId: 'demo-candidate-oliver-hughes', details: 'Oliver Hughes interview completed with training recommendations.' },
    { id: 'demo-audit-3', at: demoHrTimestamp(-7), actor: 'Registered Manager', role: 'Registered Manager', action: 'Senior candidate interview approved', collection: 'hrInterviews', recordId: 'demo-candidate-daniel-moore', details: 'Daniel Moore progressed to conditional offer.' },
    { id: 'demo-audit-4', at: demoHrTimestamp(-5), actor: 'Recruitment and HR Officer', role: 'Recruitment and HR Officer', action: 'Reference request sent', collection: 'hrReferenceChecks', recordId: 'demo-candidate-amelia-carter-reference-2', details: 'Second reference request sent to care coordinator.' },
    { id: 'demo-audit-5', at: demoHrTimestamp(-3), actor: 'Deputy Manager', role: 'Deputy Manager', action: 'DBS status updated', collection: 'hrCandidates', recordId: 'demo-candidate-sofia-patel', details: 'DBS marked awaiting result.' },
    { id: 'demo-audit-6', at: demoHrTimestamp(-2), actor: 'Recruitment and HR Officer', role: 'Recruitment and HR Officer', action: 'Employment contract generated', collection: 'documents', recordId: 'demo-contract-demo-candidate-daniel-moore', details: 'Contract draft generated from HR template.' },
    { id: 'demo-audit-7', at: demoHrTimestamp(-1), actor: 'Registered Manager', role: 'Registered Manager', action: 'Candidate converted to carer', collection: 'hrCandidates', recordId: 'demo-candidate-grace-morgan', details: 'Grace Morgan converted to active night carer demo profile.' },
  ];

  return { candidates, documents, companyDocuments, references, interviews, tasks, checklists, reminders, auditEvents };
}

function authHeaders(token?: string | null): HeadersInit {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function loadCollection<T>(collection: string, token?: string | null): Promise<T[]> {
  const response = await fetch(`/api/v1/${collection}`, { headers: authHeaders(token) });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || `Could not load ${collection}`);
  return Array.isArray(payload.data) ? payload.data : [];
}

async function saveRecord<T extends { id?: string }>(collection: string, record: T, token?: string | null) {
  const existing = Boolean(record.id);
  const response = await fetch(`/api/v1/${collection}${existing ? `/${encodeURIComponent(String(record.id))}` : ''}`, {
    method: existing ? 'PATCH' : 'POST',
    headers: {
      ...authHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(record),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || `Could not save ${collection}`);
  return payload.data;
}

function statusClass(status: string) {
  if (/VERIFIED|CLEAR|APPROVED|COMPLETE|READY/i.test(status)) return 'bg-emerald-100 text-emerald-800';
  if (/PENDING|SUBMITTED|AWAITING|IN_PROGRESS|PARTIAL/i.test(status)) return 'bg-amber-100 text-amber-800';
  if (/MISSING|REJECTED|CONCERN/i.test(status)) return 'bg-red-100 text-red-800';
  return 'bg-slate-100 text-slate-700';
}

function findRoleExamTemplate(role?: string) {
  const normalizedRole = String(role || '').toLowerCase();
  return roleExamTemplates.find((template) =>
    template.targetRoles.some((targetRole) => normalizedRole.includes(targetRole.toLowerCase()) || targetRole.toLowerCase().includes(normalizedRole))
  ) || roleExamTemplates[0];
}

function cloneExamQuestions(template: RoleExamTemplate) {
  return template.questions.map((question) => ({
    ...question,
    candidateAnswer: '',
    score: 0,
    notes: '',
  }));
}

function normalizeWords(value: string) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9\s-]/g, ' ');
}

function scoreAnswerWithAiRules(question: InterviewQuestion) {
  const answer = normalizeWords(question.candidateAnswer);
  const keywords = (question.keywords || []).map((keyword) => normalizeWords(keyword)).filter(Boolean);
  if (!answer.trim()) return { score: 0, matched: [], missing: keywords };
  const matched = keywords.filter((keyword) => answer.includes(keyword));
  const coverage = keywords.length ? matched.length / keywords.length : Math.min(1, answer.length / 180);
  const lengthBoost = answer.length > 140 ? 1 : answer.length > 80 ? 0.5 : 0;
  const score = Math.max(0, Math.min(question.maxScore || 5, Math.round(coverage * (question.maxScore || 5) + lengthBoost)));
  return { score, matched, missing: keywords.filter((keyword) => !matched.includes(keyword)) };
}

function buildAiGeneratedTemplate(candidate: Candidate): RoleExamTemplate {
  const base = findRoleExamTemplate(candidate.roleAppliedFor);
  const experience = candidate.experienceSummary || candidate.cvScreening?.experience || 'the supplied CV and application details';
  const customQuestion = examQuestion(
    `${base.id}-candidate-specific`,
    'Candidate-specific scenario',
    `Based on your application for ${candidate.roleAppliedFor}, explain how your experience with ${experience.slice(0, 120)} would help you deliver safe, person-centred care or support in this organisation.`,
    'Candidate should connect their experience to safe practice, person-centred care, escalation, confidentiality, accurate records and willingness to follow company policy.',
    ['safe', 'person-centred', 'escalation', 'confidential', 'policy'],
    false,
    'Role suitability'
  );
  return {
    ...base,
    id: `${base.id}-ai-${candidate.id}`,
    title: `AI-generated ${base.title} for ${candidate.fullName}`,
    durationMinutes: base.durationMinutes,
    questions: [...base.questions, customQuestion],
    aiInstructions: [
      ...base.aiInstructions,
      `Adapt follow-up prompts to ${candidate.fullName}'s role, availability and CV gaps.`,
      'Record only evidence relevant to the role and avoid assumptions about protected characteristics.',
    ],
  };
}

function templateActionLabel(template: CompanyDocument) {
  const title = `${template.title} ${template.documentType}`.toLowerCase();
  if (title.includes('reference')) return 'Send reference request';
  if (title.includes('contract')) return 'Generate contract';
  if (title.includes('senior')) return 'Load senior exam';
  if (title.includes('exam')) return 'Load exam';
  return 'Use template';
}

function templatePreviewLines(template: CompanyDocument, candidate: Candidate | null) {
  const title = `${template.title} ${template.documentType}`.toLowerCase();
  if (title.includes('reference')) {
    return [
      `Prepared for: ${candidate?.fullName || 'selected candidate'}`,
      'Action: sends reference requests through the HR reference endpoint and records status, date and audit trail.',
      'Template content: factual employment dates, reliability, conduct, safeguarding concerns and suitability for care work.',
      'Data rule: referees are asked not to include unnecessary special category information.',
    ];
  }
  if (title.includes('contract')) {
    return [
      `Prepared for: ${candidate?.fullName || 'selected candidate'}`,
      'Action: generates an employment contract draft from the candidate record and saves it in Documents.',
      'Template fields: candidate name, role, branch, start date, hourly rate, probation note and signature status.',
      'Legal rule: HR or Registered Manager must review the draft before signature.',
    ];
  }
  const roleTemplate = title.includes('senior')
    ? roleExamTemplates.find((item) => item.id === 'exam-senior-carer') || roleExamTemplates[0]
    : findRoleExamTemplate(candidate?.roleAppliedFor);
  return [
    `Exam: ${roleTemplate.title}`,
    `Rules: ${roleTemplate.durationMinutes} minutes, ${roleTemplate.passMark}% pass mark, maximum ${roleTemplate.maxWrongAnswersAllowed} weak answers and ${roleTemplate.maxCriticalErrorsAllowed} critical errors.`,
    `Questions: ${roleTemplate.questions.length} role-specific questions covering ${roleTemplate.questions.map((question) => question.category).slice(0, 5).join(', ')}.`,
    `Decision owner: ${roleTemplate.finalDecisionOwner}. AI scoring is advisory and human approval is required.`,
  ];
}

function scrollToExamWorkspace() {
  window.setTimeout(() => {
    document.getElementById('interview-exam-workspace')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 80);
}

export const RecruitmentHR: React.FC = () => {
  const { token } = useAuth();
  const { language } = useProductUX();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const aiExamCandidateId = searchParams.get('aiExamCandidate') || '';
  const isAiExamPage = Boolean(aiExamCandidateId);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [documents, setDocuments] = useState<CandidateDocument[]>([]);
  const [companyDocuments, setCompanyDocuments] = useState<CompanyDocument[]>([]);
  const [references, setReferences] = useState<ReferenceCheck[]>([]);
  const [interviews, setInterviews] = useState<HrInterview[]>([]);
  const [tasks, setTasks] = useState<HrTask[]>([]);
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [reminders, setReminders] = useState<ReminderLog[]>([]);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [cvText, setCvText] = useState('');
  const [cvFileName, setCvFileName] = useState('');
  const [isScreeningCv, setIsScreeningCv] = useState(false);
  const [interviewQuestions, setInterviewQuestions] = useState<InterviewQuestion[]>(interviewQuestionTemplates);
  const [interviewNotes, setInterviewNotes] = useState('');
  const [candidateDeclaration, setCandidateDeclaration] = useState('');
  const [interviewerName, setInterviewerName] = useState('Recruitment and HR Officer');
  const [isSavingInterview, setIsSavingInterview] = useState(false);
  const [selectedExamTemplateId, setSelectedExamTemplateId] = useState(roleExamTemplates[0].id);
  const [activeExamTemplate, setActiveExamTemplate] = useState<RoleExamTemplate>(roleExamTemplates[0]);
  const [examStartedAt, setExamStartedAt] = useState('');
  const [aiExamNarrative, setAiExamNarrative] = useState('');
  const [isGeneratingExam, setIsGeneratingExam] = useState(false);
  const [startedAiExamCandidateId, setStartedAiExamCandidateId] = useState('');
  const [isApplyingTemplate, setIsApplyingTemplate] = useState(false);
  const [previewTemplateId, setPreviewTemplateId] = useState('');
  const [contractStartDate, setContractStartDate] = useState('');
  const [contractHourlyRate, setContractHourlyRate] = useState('12.50');
  const [lastCarerLogin, setLastCarerLogin] = useState<{ email: string; temporaryPassword: string } | null>(null);

  const selectedCandidate =
    candidates.find((candidate) => candidate.id === (aiExamCandidateId || selectedId)) ||
    candidates.find((candidate) => candidate.id === selectedId) ||
    candidates[0] ||
    null;
  const selectedDocuments = documents.filter((documentItem) => documentItem.candidateId === selectedCandidate?.id);
  const selectedReferences = references.filter((reference) => reference.candidateId === selectedCandidate?.id);
  const selectedTasks = tasks.filter((task) => task.candidateId === selectedCandidate?.id);
  const selectedChecklist = checklists.find((checklist) => checklist.candidateId === selectedCandidate?.id);
  const selectedTrainingItems = useMemo(() => {
    const existing = selectedChecklist?.items || [];
    return minimumTraining.map((label) => existing.find((item) => item.label.toLowerCase() === label.toLowerCase()) || { label, completed: false, required: true });
  }, [selectedChecklist]);
  const selectedContract = companyDocuments
    .filter((documentItem) => documentItem.candidateId === selectedCandidate?.id && documentItem.documentType === 'Employment Contract')
    .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())[0] || null;
  const selectedReminders = reminders.filter((reminder) => reminder.candidateId === selectedCandidate?.id || String(reminder.message || '').includes(selectedCandidate?.fullName || ''));
  const selectedAuditEvents = auditEvents.filter((event) =>
    event.recordId === selectedCandidate?.id ||
    String(event.details || '').includes(selectedCandidate?.fullName || '') ||
    selectedReferences.some((reference) => reference.id === event.recordId) ||
    selectedContract?.id === event.recordId
  ).slice(0, 12);
  const selectedInterview = interviews
    .filter((interview) => interview.candidateId === selectedCandidate?.id)
    .sort((a, b) => new Date(b.interviewDate || 0).getTime() - new Date(a.interviewDate || 0).getTime())[0] || null;
  const interviewScore = interviewQuestions.reduce((sum, question) => sum + Number(question.score || 0), 0);
  const interviewMaxScore = interviewQuestions.reduce((sum, question) => sum + Number(question.maxScore || 5), 0);
  const interviewPercentage = interviewMaxScore ? Math.round((interviewScore / interviewMaxScore) * 100) : 0;
  const interviewRecommendation = interviewPercentage >= 80
    ? 'PROGRESS_TO_CONDITIONAL_OFFER'
    : interviewPercentage >= 65
      ? 'PROGRESS_WITH_TRAINING_PLAN'
      : interviewPercentage >= 50
        ? 'SECOND_INTERVIEW_OR_SHADOWING'
        : 'DO_NOT_PROGRESS';
  const weakAnswers = interviewQuestions.filter((question) => Number(question.score || 0) < Math.ceil((question.maxScore || 5) * 0.6));
  const criticalErrors = interviewQuestions.filter((question) => question.critical && Number(question.score || 0) < Math.ceil((question.maxScore || 5) * 0.6));
  const examRuleStatus = {
    wrongAnswers: weakAnswers.length,
    criticalErrors: criticalErrors.length,
    wrongAnswerLimitExceeded: weakAnswers.length > activeExamTemplate.maxWrongAnswersAllowed,
    criticalLimitExceeded: criticalErrors.length > activeExamTemplate.maxCriticalErrorsAllowed,
    passMarkMet: interviewPercentage >= activeExamTemplate.passMark,
  };
  const examCanProgress = examRuleStatus.passMarkMet && !examRuleStatus.wrongAnswerLimitExceeded && !examRuleStatus.criticalLimitExceeded;

  const metrics = useMemo(() => {
    const ready = candidates.filter((candidate) => candidate.pipelineStage === 'Ready to Start').length;
    const blocked = candidates.filter((candidate) => /PENDING|AWAITING|NOT_STARTED/i.test(`${candidate.dbsStatus} ${candidate.referenceStatus} ${candidate.rightToWorkStatus}`)).length;
    const missingDocs = documents.filter((documentItem) => /MISSING|PENDING/i.test(documentItem.status)).length;
    return { ready, blocked, missingDocs };
  }, [candidates, documents]);
  const hrTemplates = useMemo(() => {
    const templates = companyDocuments.filter((documentItem) => /template/i.test(`${documentItem.documentType} ${documentItem.title}`));
    if (templates.length) return templates;
    return createDemoRecruitmentDataset().companyDocuments.filter((documentItem) => /template/i.test(`${documentItem.documentType} ${documentItem.title}`));
  }, [companyDocuments]);
  const previewTemplate = hrTemplates.find((template) => template.id === previewTemplateId) || hrTemplates[0] || null;
  const hrCompliance = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const inProcess = candidates.filter((candidate) => !['Ready to Start', 'Rejected'].includes(candidate.pipelineStage)).length;
    const missingDocuments = documents.filter((documentItem) => /MISSING|PENDING/i.test(documentItem.status)).length;
    const expiredTraining = checklists.filter((checklist) => checklist.status !== 'READY' && checklist.status !== 'COMPLETE').length;
    const expiredDbs = documents.filter((documentItem) => /DBS/i.test(documentItem.documentType) && documentItem.expiryDate && documentItem.expiryDate < today).length +
      candidates.filter((candidate) => /EXPIRED/i.test(candidate.dbsStatus)).length;
    const unsignedContracts = companyDocuments.filter((documentItem) => documentItem.documentType === 'Employment Contract' && documentItem.status !== 'SIGNED').length;
    const carersWithoutRightToWork = candidates.filter((candidate) => candidate.convertedCarerId && !/VERIFIED/i.test(candidate.rightToWorkStatus)).length;
    const hiringAges = candidates
      .filter((candidate) => candidate.createdAt && candidate.pipelineStage === 'Ready to Start')
      .map((candidate) => Math.max(1, Math.round((Date.now() - new Date(candidate.createdAt as string).getTime()) / 86400000)));
    const averageHireDays = hiringAges.length ? Math.round(hiringAges.reduce((sum, days) => sum + days, 0) / hiringAges.length) : 0;
    return { inProcess, missingDocuments, expiredTraining, expiredDbs, unsignedContracts, carersWithoutRightToWork, averageHireDays };
  }, [candidates, documents, checklists, companyDocuments]);

  const applyDemoDataset = (showToast = true) => {
    const demo = createDemoRecruitmentDataset();
    setCandidates(demo.candidates);
    setDocuments(demo.documents);
    setCompanyDocuments(demo.companyDocuments);
    setReferences(demo.references);
    setInterviews(demo.interviews);
    setTasks(demo.tasks);
    setChecklists(demo.checklists);
    setReminders(demo.reminders);
    setAuditEvents(demo.auditEvents);
    setSelectedId((current) => demo.candidates.some((candidate) => candidate.id === current) ? current : demo.candidates[0]?.id || '');
    setIsLoading(false);
    if (showToast) {
      toast.success('Complete fictitious Recruitment & HR dataset loaded.');
    }
  };

  const load = async () => {
    setIsLoading(true);
    try {
      const [nextCandidates, nextDocuments, nextCompanyDocuments, nextReferences, nextInterviews, nextTasks, nextChecklists, nextReminders, nextAuditEvents] = await Promise.all([
        loadCollection<Candidate>('hrCandidates', token),
        loadCollection<CandidateDocument>('hrCandidateDocuments', token),
        loadCollection<CompanyDocument>('documents', token),
        loadCollection<ReferenceCheck>('hrReferenceChecks', token),
        loadCollection<HrInterview>('hrInterviews', token),
        loadCollection<HrTask>('hrTasks', token),
        loadCollection<Checklist>('hrOnboardingChecklists', token),
        loadCollection<ReminderLog>('reminderLogs', token),
        loadCollection<AuditEvent>('auditEvents', token),
      ]);
      if (!nextCandidates.length) {
        applyDemoDataset(false);
        toast.success('Demo Recruitment & HR data loaded because the database is empty.');
        return;
      }
      setCandidates(nextCandidates);
      setDocuments(nextDocuments);
      setCompanyDocuments(nextCompanyDocuments);
      setReferences(nextReferences);
      setInterviews(nextInterviews);
      setTasks(nextTasks);
      setChecklists(nextChecklists);
      setReminders(nextReminders);
      setAuditEvents(nextAuditEvents);
      setSelectedId((current) => nextCandidates.some((candidate) => candidate.id === current) ? current : nextCandidates[0]?.id || '');
    } catch (error) {
      applyDemoDataset(false);
      toast.error(error instanceof Error ? `${error.message}. Demo HR data loaded for preview.` : 'Could not load Recruitment & HR data. Demo data loaded for preview.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [token]);

  useEffect(() => {
    if (!aiExamCandidateId || !candidates.length) return;
    const candidate = candidates.find((item) => item.id === aiExamCandidateId);
    if (!candidate) {
      toast.error('Candidate not found for AI exam.');
      navigate('/recruitment-hr', { replace: true });
      return;
    }
    if (selectedId !== candidate.id) {
      setSelectedId(candidate.id);
    }
  }, [aiExamCandidateId, candidates, navigate, selectedId]);

  useEffect(() => {
    setCvText('');
    setCvFileName('');
  }, [selectedId]);

  useEffect(() => {
    if (isAiExamPage) return;
    if (selectedInterview?.questions?.length) {
      const matchingTemplate = roleExamTemplates.find((template) => template.title === selectedInterview.examTemplate || selectedInterview.examTemplate?.includes(template.title)) || findRoleExamTemplate(selectedCandidate?.roleAppliedFor);
      setActiveExamTemplate(matchingTemplate);
      setSelectedExamTemplateId(matchingTemplate.id);
      setInterviewQuestions(selectedInterview.questions);
      setInterviewNotes(selectedInterview.notes || '');
      setCandidateDeclaration(selectedInterview.candidateDeclaration || '');
      setInterviewerName(selectedInterview.interviewer || 'Recruitment and HR Officer');
      setAiExamNarrative(selectedInterview.finalRecommendation || '');
      return;
    }
    const template = findRoleExamTemplate(selectedCandidate?.roleAppliedFor);
    setActiveExamTemplate(template);
    setSelectedExamTemplateId(template.id);
    setInterviewQuestions(cloneExamQuestions(template));
    setInterviewNotes('');
    setCandidateDeclaration('');
    setInterviewerName('Recruitment and HR Officer');
    setAiExamNarrative('');
    setExamStartedAt('');
  }, [isAiExamPage, selectedInterview?.id, selectedId, selectedCandidate?.roleAppliedFor]);

  const updateCandidate = async (candidate: Candidate, patch: Partial<Candidate>) => {
    setIsSaving(true);
    try {
      const saved = await saveRecord<Candidate>('hrCandidates', { ...candidate, ...patch }, token);
      setCandidates((current) => current.map((item) => item.id === candidate.id ? saved : item));
      toast.success('Candidate updated');
      return saved;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not update candidate');
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const addCandidate = async () => {
    setIsSaving(true);
    try {
      const saved = await saveRecord<Partial<Candidate>>('hrCandidates', {
        firstName: 'New',
        lastName: 'Candidate',
        fullName: 'New Candidate',
        email: `candidate${Date.now()}@example.com`,
        phone: '',
        pipelineStage: 'Applied',
        branchId: 'branch-1',
        roleAppliedFor: 'Care Assistant',
        availability: '',
        experienceSummary: '',
      }, token);
      setCandidates((current) => [saved, ...current]);
      setSelectedId(saved.id);
      toast.success('Candidate created');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not create candidate');
    } finally {
      setIsSaving(false);
    }
  };

  const convertToCarer = async () => {
    if (!selectedCandidate) return;
    if (selectedCandidate.convertedCarerId) {
      toast('This candidate is already linked to a carer profile.');
      return;
    }
    setIsSaving(true);
    try {
      const response = await fetch(`/api/hr/candidates/${encodeURIComponent(selectedCandidate.id)}/convert-to-carer`, {
        method: 'POST',
        headers: {
          ...authHeaders(token),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ hourlyRate: Number(contractHourlyRate || 12.5) }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.blockers?.join(' | ') || payload?.error || 'Could not convert candidate');
      setCandidates((current) => current.map((candidate) => candidate.id === selectedCandidate.id ? payload.data.candidate : candidate));
      setLastCarerLogin(payload.data.login || null);
      toast.success('Candidate converted to active carer profile');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not convert candidate');
    } finally {
      setIsSaving(false);
    }
  };

  const createAiSummary = async () => {
    if (!selectedCandidate) return;
    const summary = [
      `AI screening draft for ${selectedCandidate.fullName}:`,
      selectedCandidate.experienceSummary || 'No experience summary entered yet.',
      `Recommended next step: ${selectedCandidate.dbsStatus === 'CLEAR' && selectedCandidate.referenceStatus === 'APPROVED' ? 'prepare contract and onboarding.' : 'complete DBS, references and required documents before offer.'}`,
      'Human HR approval is required before any hiring decision.',
    ].join(' ');
    await updateCandidate(selectedCandidate, { aiScreeningSummary: summary });
  };

  const handleCvFile = async (file: File | null) => {
    if (!file) return;
    setCvFileName(file.name);
    try {
      const text = await file.text();
      setCvText(text);
      toast.success('CV text loaded. Review it before running screening.');
    } catch {
      toast.error('Could not read this file. Please paste the CV text manually.');
    }
  };

  const runCvScreening = async () => {
    if (!selectedCandidate) return;
    if (cvText.trim().length < 80) {
      toast.error('Paste or upload enough CV text before screening.');
      return;
    }

    setIsScreeningCv(true);
    try {
      const response = await fetch('/api/hr/cv-screening', {
        method: 'POST',
        headers: {
          ...authHeaders(token),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          candidateId: selectedCandidate.id,
          cvText,
          fileName: cvFileName,
          language,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Could not screen CV');

      const savedCandidate = payload?.data?.candidate as Candidate;
      setCandidates((current) => current.map((candidate) => candidate.id === savedCandidate.id ? savedCandidate : candidate));
      toast.success('CV screening saved for HR review');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not screen CV');
    } finally {
      setIsScreeningCv(false);
    }
  };

  const updateInterviewQuestion = (questionId: string, patch: Partial<InterviewQuestion>) => {
    setInterviewQuestions((current) => current.map((question) => (
      question.id === questionId ? { ...question, ...patch } : question
    )));
  };

  const startExamFromTemplate = (template: RoleExamTemplate, source: 'library' | 'ai' | 'document' = 'library') => {
    setActiveExamTemplate(template);
    setSelectedExamTemplateId(template.id);
    setInterviewQuestions(cloneExamQuestions(template));
    setExamStartedAt(new Date().toISOString());
    setCandidateDeclaration('');
    setInterviewNotes([
      `${source === 'ai' ? 'AI-generated' : 'Template'} exam started for ${selectedCandidate?.fullName || 'selected candidate'}.`,
      `Template: ${template.title}. Duration: ${template.durationMinutes} minutes. Pass mark: ${template.passMark}%. Critical errors allowed: ${template.maxCriticalErrorsAllowed}.`,
      'AI may ask, score and summarise answers, but HR/manager approval remains required.',
    ].join(' '));
    setAiExamNarrative(`AI exam script prepared. Ask one question at a time, probe incomplete answers, avoid assumptions about protected characteristics, and save the result to the candidate file.`);
    toast.success(`${template.title} loaded for ${selectedCandidate?.fullName || 'candidate'}`);
    scrollToExamWorkspace();
  };

  const loadSelectedExamTemplate = () => {
    const template = roleExamTemplates.find((item) => item.id === selectedExamTemplateId) || findRoleExamTemplate(selectedCandidate?.roleAppliedFor);
    startExamFromTemplate(template);
  };

  const startAiExamForCandidate = (candidate: Candidate) => {
    const template = buildAiGeneratedTemplate(candidate);
    startExamFromTemplate(template, 'ai');
    setAiExamNarrative([
      `AI created a role-specific exam for ${candidate.fullName} applying for ${candidate.roleAppliedFor}.`,
      `It uses the base ${findRoleExamTemplate(candidate.roleAppliedFor).title} and adds one candidate-specific scenario from the CV/application summary.`,
      'The exam focuses on safe recruitment, right-to-work/DBS readiness, safeguarding, confidentiality, role competence and evidence-based answers.',
    ].join(' '));
  };

  const generateAiExamForCandidate = () => {
    if (!selectedCandidate) {
      toast.error('Select a candidate before creating an AI exam.');
      return;
    }
    setIsGeneratingExam(true);
    navigate(`/recruitment-hr?aiExamCandidate=${encodeURIComponent(selectedCandidate.id)}`);
  };

  useEffect(() => {
    if (!isAiExamPage || !selectedCandidate || startedAiExamCandidateId === selectedCandidate.id) return;
    setIsGeneratingExam(true);
    startAiExamForCandidate(selectedCandidate);
    setStartedAiExamCandidateId(selectedCandidate.id);
    setIsGeneratingExam(false);
  }, [isAiExamPage, selectedCandidate?.id, startedAiExamCandidateId]);

  const applyAiAssessment = () => {
    const assessed = interviewQuestions.map((question) => {
      const result = scoreAnswerWithAiRules(question);
      return {
        ...question,
        score: result.score,
        notes: [
          `AI assessment: matched evidence ${result.matched.length ? result.matched.join(', ') : 'none'}.`,
          result.missing.length ? `Missing: ${result.missing.join(', ')}.` : 'Key evidence covered.',
          question.critical && result.score < 3 ? 'Critical risk: manager review required.' : '',
        ].filter(Boolean).join(' '),
      };
    });
    setInterviewQuestions(assessed);
    const score = assessed.reduce((sum, question) => sum + Number(question.score || 0), 0);
    const maxScore = assessed.reduce((sum, question) => sum + Number(question.maxScore || 5), 0);
    const percentage = maxScore ? Math.round((score / maxScore) * 100) : 0;
    const criticalCount = assessed.filter((question) => question.critical && Number(question.score || 0) < 3).length;
    const weakCount = assessed.filter((question) => Number(question.score || 0) < 3).length;
    setAiExamNarrative([
      `AI assessment complete: ${score}/${maxScore} (${percentage}%).`,
      `Weak answers: ${weakCount}. Critical weak answers: ${criticalCount}.`,
      percentage >= activeExamTemplate.passMark && criticalCount <= activeExamTemplate.maxCriticalErrorsAllowed
        ? 'Recommendation: may progress only after human HR/manager approval and completion of all checks.'
        : 'Recommendation: do not progress automatically; manager review, second interview or training plan required.',
    ].join(' '));
    toast.success('AI assessment completed. Review the notes before saving.');
  };

  const runAiAssessmentAndSave = async () => {
    if (interviewQuestions.some((question) => !question.candidateAnswer.trim())) {
      toast.error('Complete every candidate answer before AI can finalise and save the exam.');
      return;
    }
    const assessed = interviewQuestions.map((question) => {
      const result = scoreAnswerWithAiRules(question);
      return {
        ...question,
        score: result.score,
        notes: [
          `AI assessment: matched evidence ${result.matched.length ? result.matched.join(', ') : 'none'}.`,
          result.missing.length ? `Missing: ${result.missing.join(', ')}.` : 'Key evidence covered.',
          question.critical && result.score < 3 ? 'Critical risk: manager review required.' : '',
        ].filter(Boolean).join(' '),
      };
    });
    const score = assessed.reduce((sum, question) => sum + Number(question.score || 0), 0);
    const maxScore = assessed.reduce((sum, question) => sum + Number(question.maxScore || 5), 0);
    const percentage = maxScore ? Math.round((score / maxScore) * 100) : 0;
    const criticalCount = assessed.filter((question) => question.critical && Number(question.score || 0) < 3).length;
    const weakCount = assessed.filter((question) => Number(question.score || 0) < 3).length;
    const narrative = [
      `AI conducted final assessment: ${score}/${maxScore} (${percentage}%).`,
      `Weak answers: ${weakCount}. Critical weak answers: ${criticalCount}.`,
      percentage >= activeExamTemplate.passMark && criticalCount <= activeExamTemplate.maxCriticalErrorsAllowed
        ? 'Recommendation: may progress only after human HR/manager approval and completion of all checks.'
        : 'Recommendation: do not progress automatically; manager review, second interview or training plan required.',
    ].join(' ');
    setInterviewQuestions(assessed);
    setAiExamNarrative(narrative);
    await saveInterviewExam(assessed, narrative);
  };

  const applyHrTemplate = async (template: CompanyDocument) => {
    if (!selectedCandidate) {
      toast.error('Select a candidate before using an HR template.');
      return;
    }
    const title = `${template.title} ${template.documentType}`.toLowerCase();
    setPreviewTemplateId(template.id);
    setIsApplyingTemplate(true);
    if (title.includes('reference')) {
      try {
        const referencesToSend = selectedReferences.length
          ? selectedReferences
          : [{
              id: `${selectedCandidate.id}-reference-${Date.now()}`,
              candidateId: selectedCandidate.id,
              refereeName: 'Referee to confirm',
              refereeEmail: 'reference@example.com',
              relationship: 'Previous manager',
              status: 'DRAFT',
              outcome: 'Created from Reference Request Email Template.',
            } as ReferenceCheck];
        let savedReferences = referencesToSend;
        if (!selectedReferences.length) {
          const saved = await saveRecord<ReferenceCheck>('hrReferenceChecks', referencesToSend[0], token);
          savedReferences = [saved];
          setReferences((current) => [saved, ...current]);
        }

        const sentReferences = await Promise.all(savedReferences.map(async (reference) => {
          const response = await fetch(`/api/hr/references/${encodeURIComponent(reference.id)}/send`, {
            method: 'POST',
            headers: authHeaders(token),
          });
          const payload = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(payload?.error || `Could not send reference request to ${reference.refereeName}`);
          return payload.data as ReferenceCheck;
        }));

        setReferences((current) => current.map((item) => sentReferences.find((sent) => sent.id === item.id) || item));
        await updateCandidate(selectedCandidate, { referenceStatus: 'SENT', pipelineStage: selectedCandidate.pipelineStage === 'Applied' ? 'References' : selectedCandidate.pipelineStage });
        toast.success(`${sentReferences.length} reference request template(s) sent and saved.`);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Could not apply reference template');
      } finally {
        setIsApplyingTemplate(false);
      }
      return;
    }
    if (title.includes('contract')) {
      try {
        await generateContract();
      } finally {
        setIsApplyingTemplate(false);
      }
      return;
    }
    const roleTemplate = title.includes('senior') ? roleExamTemplates.find((item) => item.id === 'exam-senior-carer') : findRoleExamTemplate(selectedCandidate?.roleAppliedFor);
    if (roleTemplate) startExamFromTemplate(roleTemplate, 'document');
    setIsApplyingTemplate(false);
  };

  const buildFinalInterviewRecommendation = (questions = interviewQuestions, narrative = aiExamNarrative) => {
    const localScore = questions.reduce((sum, question) => sum + Number(question.score || 0), 0);
    const localMaxScore = questions.reduce((sum, question) => sum + Number(question.maxScore || 5), 0);
    const localPercentage = localMaxScore ? Math.round((localScore / localMaxScore) * 100) : 0;
    const localWeakAnswers = questions.filter((question) => Number(question.score || 0) < Math.ceil((question.maxScore || 5) * 0.6));
    const localCriticalErrors = questions.filter((question) => question.critical && Number(question.score || 0) < Math.ceil((question.maxScore || 5) * 0.6));
    const localCanProgress = localPercentage >= activeExamTemplate.passMark &&
      localWeakAnswers.length <= activeExamTemplate.maxWrongAnswersAllowed &&
      localCriticalErrors.length <= activeExamTemplate.maxCriticalErrorsAllowed;
    const localRecommendation = localPercentage >= 80
      ? 'PROGRESS_TO_CONDITIONAL_OFFER'
      : localPercentage >= 65
        ? 'PROGRESS_WITH_TRAINING_PLAN'
        : localPercentage >= 50
          ? 'SECOND_INTERVIEW_OR_SHADOWING'
          : 'DO_NOT_PROGRESS';
    const missingAnswers = questions.filter((question) => !question.candidateAnswer.trim()).length;
    const lowCriticalScores = questions.filter((question) =>
      ['Safeguarding', 'Medication safety', 'Moving and handling', 'Emergency response'].includes(question.category) &&
      Number(question.score || 0) < 3
    );
    const actions = [
      missingAnswers ? `${missingAnswers} answer(s) still need to be completed before final decision.` : '',
      lowCriticalScores.length ? `Critical areas need review: ${lowCriticalScores.map((question) => question.category).join(', ')}.` : '',
      localPercentage >= 65 ? 'Candidate may progress only after HR/manager approval and completion of outstanding checks.' : 'Candidate should not progress without a manager review and documented reason.',
    ].filter(Boolean);

    return [
      `Exam score: ${localScore}/${localMaxScore} (${localPercentage}%).`,
      `Automated recommendation: ${localCanProgress ? localRecommendation : 'MANAGER_REVIEW_REQUIRED'}.`,
      `Template: ${activeExamTemplate.title}. Duration: ${activeExamTemplate.durationMinutes} minutes. Pass mark: ${activeExamTemplate.passMark}%. Wrong answers: ${localWeakAnswers.length}/${activeExamTemplate.maxWrongAnswersAllowed}. Critical errors: ${localCriticalErrors.length}/${activeExamTemplate.maxCriticalErrorsAllowed}.`,
      localCanProgress ? 'Template rules: pass threshold met.' : 'Template rules: progression blocked until human review or re-assessment.',
      narrative,
      ...actions,
      'Final recruitment decision must be made by HR/Registered Manager, not automatically by the system.',
    ].join(' ');
  };

  const saveInterviewExam = async (questionsToSave = interviewQuestions, narrativeToSave = aiExamNarrative) => {
    if (!selectedCandidate) return null;
    const localScore = questionsToSave.reduce((sum, question) => sum + Number(question.score || 0), 0);
    const localMaxScore = questionsToSave.reduce((sum, question) => sum + Number(question.maxScore || 5), 0);
    const localPercentage = localMaxScore ? Math.round((localScore / localMaxScore) * 100) : 0;
    const localWeakAnswers = questionsToSave.filter((question) => Number(question.score || 0) < Math.ceil((question.maxScore || 5) * 0.6));
    const localCriticalErrors = questionsToSave.filter((question) => question.critical && Number(question.score || 0) < Math.ceil((question.maxScore || 5) * 0.6));
    const localCanProgress = localPercentage >= activeExamTemplate.passMark &&
      localWeakAnswers.length <= activeExamTemplate.maxWrongAnswersAllowed &&
      localCriticalErrors.length <= activeExamTemplate.maxCriticalErrorsAllowed;
    const localRecommendation = localPercentage >= 80
      ? 'PROGRESS_TO_CONDITIONAL_OFFER'
      : localPercentage >= 65
        ? 'PROGRESS_WITH_TRAINING_PLAN'
        : localPercentage >= 50
          ? 'SECOND_INTERVIEW_OR_SHADOWING'
          : 'DO_NOT_PROGRESS';
    setIsSavingInterview(true);
    try {
      const record: HrInterview = {
        ...(selectedInterview || {}),
        candidateId: selectedCandidate.id,
        interviewDate: selectedInterview?.interviewDate || new Date().toISOString().slice(0, 10),
        interviewer: interviewerName || 'Recruitment and HR Officer',
        score: localScore,
        maxScore: localMaxScore,
        passMark: activeExamTemplate.passMark,
        percentage: localPercentage,
        recommendation: localCanProgress ? localRecommendation : 'MANAGER_REVIEW_REQUIRED',
        status: questionsToSave.every((question) => question.candidateAnswer.trim()) ? 'COMPLETED_PENDING_HUMAN_DECISION' : 'IN_PROGRESS',
        examTemplate: activeExamTemplate.title,
        questions: questionsToSave,
        notes: [
          interviewNotes,
          `Rules: ${activeExamTemplate.rules.join(' ')}`,
          `Legal basis checked: ${activeExamTemplate.legalBasis.join(' ')}`,
        ].filter(Boolean).join('\n\n'),
        candidateDeclaration,
        finalRecommendation: buildFinalInterviewRecommendation(questionsToSave, narrativeToSave),
      };
      const saved = await saveRecord<HrInterview>('hrInterviews', record, token);
      setInterviews((current) => current.some((interview) => interview.id === saved.id)
        ? current.map((interview) => interview.id === saved.id ? saved : interview)
        : [saved, ...current]);
      if (selectedCandidate.pipelineStage !== 'Ready to Start') {
        const savedCandidate = await saveRecord<Candidate>('hrCandidates', { ...selectedCandidate, pipelineStage: 'Interview' }, token);
        setCandidates((current) => current.map((candidate) => candidate.id === selectedCandidate.id ? savedCandidate : candidate));
      }
      toast.success('Interview exam record saved');
      return saved as HrInterview;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save interview exam');
      return null;
    } finally {
      setIsSavingInterview(false);
    }
  };

  const downloadInterviewPdf = async () => {
    const interview = selectedInterview || await saveInterviewExam();
    if (!interview?.id) return;

    try {
      const response = await fetch(`/api/hr/interviews/${encodeURIComponent(interview.id)}/pdf`, {
        headers: authHeaders(token),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || 'Could not generate interview PDF');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedCandidate?.fullName || 'candidate'}-interview-record.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Interview PDF downloaded');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not download interview PDF');
    }
  };

  const sendReferenceRequest = async (reference: ReferenceCheck) => {
    try {
      const response = await fetch(`/api/hr/references/${encodeURIComponent(reference.id)}/send`, {
        method: 'POST',
        headers: authHeaders(token),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Could not send reference request');
      setReferences((current) => current.map((item) => item.id === reference.id ? payload.data : item));
      toast.success('Reference request marked as sent');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not send reference request');
    }
  };

  const updateReference = async (reference: ReferenceCheck, patch: Partial<ReferenceCheck>) => {
    try {
      const saved = await saveRecord<ReferenceCheck>('hrReferenceChecks', { ...reference, ...patch }, token);
      setReferences((current) => current.map((item) => item.id === reference.id ? saved : item));
      if (selectedCandidate) {
        const nextReferences = references.map((item) => item.id === reference.id ? saved : item).filter((item) => item.candidateId === selectedCandidate.id);
        const referenceStatus = nextReferences.some((item) => item.status === 'CONCERN')
          ? 'CONCERN'
          : nextReferences.every((item) => item.status === 'APPROVED')
            ? 'APPROVED'
            : nextReferences.some((item) => ['SENT', 'REPLIED'].includes(item.status))
              ? 'SENT'
              : selectedCandidate.referenceStatus;
        await updateCandidate(selectedCandidate, { referenceStatus });
      }
      toast.success('Reference response saved');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save reference');
    }
  };

  const updateDbs = async (status: string, expiryDate?: string) => {
    if (!selectedCandidate) return;
    try {
      await updateCandidate(selectedCandidate, { dbsStatus: status });
      const dbsDocument = selectedDocuments.find((documentItem) => /DBS/i.test(documentItem.documentType));
      if (dbsDocument && expiryDate !== undefined) {
        const saved = await saveRecord<CandidateDocument>('hrCandidateDocuments', {
          ...dbsDocument,
          status: status === 'CLEAR' ? 'VERIFIED' : status,
          expiryDate,
        }, token);
        setDocuments((current) => current.map((item) => item.id === saved.id ? saved : item));
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not update DBS');
    }
  };

  const saveTrainingChecklist = async (items: Array<{ label: string; completed: boolean; required: boolean }>) => {
    if (!selectedCandidate) return;
    try {
      const complete = minimumTraining.every((label) => items.some((item) => item.label.toLowerCase() === label.toLowerCase() && item.completed));
      const saved = await saveRecord<Checklist>('hrOnboardingChecklists', {
        ...(selectedChecklist || { candidateId: selectedCandidate.id, items: [], status: 'IN_PROGRESS' }),
        candidateId: selectedCandidate.id,
        items,
        status: complete ? 'COMPLETE' : 'IN_PROGRESS',
        completedAt: complete ? new Date().toISOString() : '',
      }, token);
      setChecklists((current) => current.some((item) => item.id === saved.id)
        ? current.map((item) => item.id === saved.id ? saved : item)
        : [saved, ...current]);
      await updateCandidate(selectedCandidate, { trainingStatus: complete ? 'COMPLETE' : 'IN_PROGRESS' });
      toast.success(complete ? 'Mandatory training complete' : 'Training checklist updated');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save training checklist');
    }
  };

  const generateContract = async () => {
    if (!selectedCandidate) return;
    try {
      const response = await fetch(`/api/hr/candidates/${encodeURIComponent(selectedCandidate.id)}/contract`, {
        method: 'POST',
        headers: {
          ...authHeaders(token),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ startDate: contractStartDate, hourlyRate: Number(contractHourlyRate || 12.5) }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Could not generate contract');
      setCompanyDocuments((current) => [payload.data.document, ...current]);
      setCandidates((current) => current.map((candidate) => candidate.id === selectedCandidate.id ? payload.data.candidate : candidate));
      toast.success('Employment contract generated and saved to documents');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not generate contract');
    }
  };

  const signContract = async () => {
    if (!selectedContract || !selectedCandidate) return;
    try {
      const response = await fetch(`/api/hr/contracts/${encodeURIComponent(selectedContract.id)}/sign`, {
        method: 'POST',
        headers: {
          ...authHeaders(token),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ signerName: selectedCandidate.fullName, signerRole: 'Candidate', signatureText: selectedCandidate.fullName }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Could not sign contract');
      setCompanyDocuments((current) => current.map((documentItem) => documentItem.id === selectedContract.id ? payload.data.document : documentItem));
      toast.success('Contract digitally signed and stored');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not sign contract');
    }
  };

  const createAutomatedReminders = async () => {
    if (!selectedCandidate) return;
    try {
      const response = await fetch(`/api/hr/candidates/${encodeURIComponent(selectedCandidate.id)}/reminders`, {
        method: 'POST',
        headers: authHeaders(token),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Could not create reminders');
      setReminders((current) => [...payload.data, ...current]);
      toast.success(`${payload.total || 0} HR reminder(s) created`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not create reminders');
    }
  };

  if (isAiExamPage) {
    if (isLoading) {
      return (
        <div className="grid min-h-[60vh] place-items-center rounded-lg border border-slate-200 bg-white p-8 text-slate-600 shadow-sm">
          Loading AI-assisted candidate exam...
        </div>
      );
    }

    if (!selectedCandidate) {
      return (
        <section className="rounded-lg border border-red-200 bg-red-50 p-6 text-red-800 shadow-sm">
          <h1 className="text-2xl font-bold">Candidate not found</h1>
          <p className="mt-2 text-sm">Return to Recruitment & HR and select a valid candidate before starting an AI exam.</p>
          <button
            type="button"
            onClick={() => navigate('/recruitment-hr')}
            className="mt-4 rounded-md bg-white px-4 py-2 text-sm font-semibold text-red-800 shadow-sm hover:bg-red-100"
          >
            Back to Recruitment & HR
          </button>
        </section>
      );
    }

    return (
      <div className="space-y-6">
        <section className="rounded-lg border border-purple-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="flex gap-4">
              <div className="grid h-14 w-14 place-items-center rounded-lg bg-purple-100 text-purple-700">
                <Bot className="h-7 w-7" />
              </div>
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-purple-800">Recruitment AI exam</p>
                <h1 className="mt-1 text-3xl font-bold text-slate-950">AI-assisted candidate exam</h1>
                <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600">
                  The exam starts automatically for {selectedCandidate.fullName}. The AI assistant adapts the questions to the role, scores evidence, records risks and keeps HR in control of the final decision.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate('/recruitment-hr')}
              className="inline-flex items-center justify-center rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Back to HR command centre
            </button>
          </div>
          <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <DetailPill label="Candidate" value={selectedCandidate.fullName} />
            <DetailPill label="Role" value={selectedCandidate.roleAppliedFor} />
            <DetailPill label="AI template" value={activeExamTemplate.title} />
            <DetailPill label="Started" value={examStartedAt ? new Date(examStartedAt).toLocaleString() : 'Starting...'} />
          </div>
        </section>

        <section id="interview-exam-workspace" className="scroll-mt-24 rounded-lg border border-purple-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-2xl font-bold text-slate-950">
                <ClipboardCheck className="h-6 w-6 text-purple-700" />
                AI-assisted exam in progress
              </h2>
              <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600">
                Ask one question at a time, record the candidate answer, then use AI assessment to score against the expected UK recruitment evidence. Save the exam to the candidate file when HR has reviewed it.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={applyAiAssessment}
                className="inline-flex items-center gap-2 rounded-md border border-purple-200 px-4 py-2 text-sm font-semibold text-purple-800 hover:bg-purple-50"
              >
                <Bot className="h-4 w-4" />
                AI assess
              </button>
              <button
                type="button"
                onClick={() => void runAiAssessmentAndSave()}
                disabled={isSavingInterview}
                className="inline-flex items-center gap-2 rounded-md border border-emerald-200 px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-50 disabled:opacity-60"
              >
                <Sparkles className="h-4 w-4" />
                AI assess and save
              </button>
              <button
                type="button"
                onClick={() => void saveInterviewExam()}
                disabled={isSavingInterview}
                className="inline-flex items-center gap-2 rounded-md bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60"
              >
                <Save className="h-4 w-4" />
                Save exam
              </button>
              <button
                type="button"
                onClick={() => void downloadInterviewPdf()}
                className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <Download className="h-4 w-4" />
                PDF record
              </button>
            </div>
          </div>

          <div className="mt-5 grid gap-3 lg:grid-cols-4">
            <div className="rounded-lg border border-purple-100 bg-purple-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-purple-800">Active template</p>
              <p className="mt-2 font-bold text-slate-950">{activeExamTemplate.title}</p>
              <p className="mt-1 text-sm text-slate-700">{activeExamTemplate.durationMinutes} minutes</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Score</p>
              <p className="mt-2 text-2xl font-bold text-slate-950">{interviewScore}/{interviewMaxScore}</p>
              <p className="text-sm text-slate-600">{interviewPercentage}%</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Rule result</p>
              <p className={`mt-2 font-bold ${examCanProgress ? 'text-emerald-700' : 'text-amber-800'}`}>
                {examCanProgress ? 'Pass threshold met' : 'Manager review required'}
              </p>
              <p className="mt-1 text-sm text-slate-600">Weak {examRuleStatus.wrongAnswers}/{activeExamTemplate.maxWrongAnswersAllowed}. Critical {examRuleStatus.criticalErrors}/{activeExamTemplate.maxCriticalErrorsAllowed}.</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <Award className="h-4 w-4 text-purple-700" />
                Recommendation
              </p>
              <p className="mt-2 text-sm font-bold text-slate-950">{interviewRecommendation}</p>
              <p className="mt-1 text-xs text-slate-600">Human approval required</p>
            </div>
          </div>

          {aiExamNarrative && (
            <div className="mt-5 rounded-lg border border-purple-200 bg-purple-50/60 p-4">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-purple-800">
                <Bot className="h-4 w-4" />
                AI exam guidance
              </p>
              <p className="mt-2 text-sm leading-6 text-slate-700">{aiExamNarrative}</p>
            </div>
          )}

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <label className="space-y-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Interviewer</span>
              <input
                value={interviewerName}
                onChange={(event) => setInterviewerName(event.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="space-y-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Candidate declaration</span>
              <input
                value={candidateDeclaration}
                onChange={(event) => setCandidateDeclaration(event.target.value)}
                placeholder="Example: I confirm these answers are my own and true to the best of my knowledge."
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
          </div>

          <div className="mt-5 space-y-4">
            {interviewQuestions.map((question, index) => (
              <div key={question.id} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-purple-800">{index + 1}. {question.category}</p>
                      {question.critical && <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">Critical</span>}
                      {question.legalArea && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">{question.legalArea}</span>}
                    </div>
                    <p className="mt-2 font-semibold leading-6 text-slate-950">{question.question}</p>
                    <p className="mt-2 text-xs leading-5 text-slate-500">Expected evidence: {question.expectedAnswer}</p>
                    {question.keywords?.length ? (
                      <p className="mt-2 text-xs leading-5 text-slate-500">AI scoring evidence: {question.keywords.join(', ')}</p>
                    ) : null}
                  </div>
                  <label className="w-32 space-y-1">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Score / {question.maxScore}</span>
                    <input
                      type="number"
                      min={0}
                      max={question.maxScore}
                      value={question.score}
                      onChange={(event) => updateInterviewQuestion(question.id, { score: Math.max(0, Math.min(question.maxScore, Number(event.target.value || 0))) })}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    />
                  </label>
                </div>
                <div className="mt-4 grid gap-3 lg:grid-cols-2">
                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Candidate answer</span>
                    <textarea
                      value={question.candidateAnswer}
                      onChange={(event) => updateInterviewQuestion(question.id, { candidateAnswer: event.target.value })}
                      rows={3}
                      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">HR notes</span>
                    <textarea
                      value={question.notes}
                      onChange={(event) => updateInterviewQuestion(question.id, { notes: event.target.value })}
                      rows={3}
                      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>

          <label className="mt-5 block space-y-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Overall interview notes and final HR recommendation</span>
            <textarea
              value={interviewNotes}
              onChange={(event) => setInterviewNotes(event.target.value)}
              rows={4}
              placeholder="Record strengths, risks, training needs, conditions, and final human recommendation."
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </label>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex gap-4">
            <div className="grid h-14 w-14 place-items-center rounded-lg bg-purple-100 text-purple-700">
              <BriefcaseBusiness className="h-7 w-7" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-purple-800">Recruitment and HR</p>
              <h1 className="mt-1 text-3xl font-bold text-slate-950">Recruitment & HR Command Centre</h1>
              <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600">
                Manage candidates, document checks, DBS, references, onboarding, contracts and conversion into active carers.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={() => applyDemoDataset(true)} className="inline-flex items-center gap-2 rounded-md border border-purple-200 px-4 py-2 text-sm font-semibold text-purple-800 hover:bg-purple-50">
              <ClipboardCheck className="h-4 w-4" />
              Load demo HR data
            </button>
            <button type="button" onClick={() => void load()} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
            <button type="button" onClick={() => void addCandidate()} disabled={isSaving} className="inline-flex items-center gap-2 rounded-md bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60">
              <UserPlus className="h-4 w-4" />
              Add candidate
            </button>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-4">
        <MetricCard label="Open candidates" value={candidates.length} icon={<Users className="h-5 w-5" />} />
        <MetricCard label="Ready to start" value={metrics.ready} icon={<CheckCircle2 className="h-5 w-5" />} />
        <MetricCard label="Blocked checks" value={metrics.blocked} icon={<FileText className="h-5 w-5" />} />
        <MetricCard label="Missing documents" value={metrics.missingDocs} icon={<FileText className="h-5 w-5" />} />
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-950">HR compliance dashboard</h2>
            <p className="mt-1 text-sm text-slate-600">Live recruitment control for missing evidence, expired checks, unsigned contracts and hiring speed.</p>
          </div>
          <button type="button" onClick={() => selectedCandidate && void createAutomatedReminders()} className="inline-flex items-center gap-2 rounded-md border border-purple-200 px-4 py-2 text-sm font-semibold text-purple-800 hover:bg-purple-50">
            <Mail className="h-4 w-4" />
            Create reminders for selected candidate
          </button>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-4 xl:grid-cols-7">
          <ComplianceTile label="Candidates in process" value={hrCompliance.inProcess} />
          <ComplianceTile label="Missing documents" value={hrCompliance.missingDocuments} alert={hrCompliance.missingDocuments > 0} />
          <ComplianceTile label="Training incomplete" value={hrCompliance.expiredTraining} alert={hrCompliance.expiredTraining > 0} />
          <ComplianceTile label="DBS expired/blocked" value={hrCompliance.expiredDbs} alert={hrCompliance.expiredDbs > 0} />
          <ComplianceTile label="Unsigned contracts" value={hrCompliance.unsignedContracts} alert={hrCompliance.unsignedContracts > 0} />
          <ComplianceTile label="RTW issues" value={hrCompliance.carersWithoutRightToWork} alert={hrCompliance.carersWithoutRightToWork > 0} />
          <ComplianceTile label="Avg hire days" value={hrCompliance.averageHireDays || '-'} />
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-950">Exam and HR templates</h2>
            <p className="mt-1 text-sm text-slate-600">Ready-to-use exam, reference and contract templates. Exams include UK safe recruitment rules, timer, pass mark and critical-error controls.</p>
          </div>
          <span className="inline-flex w-fit items-center gap-2 rounded-full bg-purple-50 px-3 py-1 text-xs font-semibold text-purple-800">
            <ClipboardCheck className="h-4 w-4" />
            {hrTemplates.length} templates
          </span>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {hrTemplates.map((template) => (
            <div key={template.id} className={`rounded-lg border p-4 ${previewTemplate?.id === template.id ? 'border-purple-300 bg-purple-50' : 'border-slate-200 bg-slate-50'}`}>
              <p className="font-semibold text-slate-950">{template.title}</p>
              <p className="mt-1 text-xs text-slate-500">{template.documentType} - {template.category}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusClass(template.status)}`}>{template.status}</span>
                <button
                  type="button"
                  onClick={() => setPreviewTemplateId(template.id)}
                  className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <FileText className="h-3.5 w-3.5" />
                  Preview
                </button>
                <button
                  type="button"
                  onClick={() => void applyHrTemplate(template)}
                  disabled={isApplyingTemplate || !selectedCandidate}
                  className="inline-flex items-center gap-1 rounded-md border border-purple-200 bg-white px-3 py-1.5 text-xs font-semibold text-purple-800 hover:bg-purple-50"
                >
                  <PlayCircle className="h-3.5 w-3.5" />
                  {templateActionLabel(template)}
                </button>
              </div>
            </div>
          ))}
        </div>
        {previewTemplate && (
          <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-purple-800">Selected template preview</p>
                <h3 className="mt-1 text-lg font-bold text-slate-950">{previewTemplate.title}</h3>
                <p className="mt-1 text-sm text-slate-600">{previewTemplate.documentType} for {selectedCandidate?.fullName || 'the selected candidate'}.</p>
              </div>
              <button
                type="button"
                onClick={() => void applyHrTemplate(previewTemplate)}
                disabled={isApplyingTemplate || !selectedCandidate}
                className="inline-flex items-center gap-2 rounded-md bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60"
              >
                <PlayCircle className="h-4 w-4" />
                {isApplyingTemplate ? 'Applying...' : templateActionLabel(previewTemplate)}
              </button>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {templatePreviewLines(previewTemplate, selectedCandidate).map((line) => (
                <div key={line} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm leading-6 text-slate-700">
                  {line}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="mt-5 rounded-lg border border-purple-100 bg-purple-50/50 p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h3 className="font-bold text-slate-950">Role exam library</h3>
              <p className="mt-1 text-sm leading-6 text-slate-700">
                Select a role-specific exam for carers, supervisors, managers, HR, finance, technical admin or drivers. AI can also adapt the exam to the selected candidate.
              </p>
            </div>
            <button
              type="button"
              onClick={generateAiExamForCandidate}
              disabled={!selectedCandidate || isGeneratingExam}
              className="inline-flex items-center gap-2 rounded-md bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60"
            >
              <Sparkles className="h-4 w-4" />
              {isGeneratingExam ? 'Creating AI exam...' : 'AI exam for selected candidate'}
            </button>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            {roleExamTemplates.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => startExamFromTemplate(template)}
                className={`rounded-lg border p-4 text-left transition ${activeExamTemplate.id === template.id ? 'border-purple-400 bg-white shadow-sm' : 'border-purple-100 bg-white/70 hover:bg-white'}`}
              >
                <p className="font-semibold text-slate-950">{template.title}</p>
                <p className="mt-2 text-xs leading-5 text-slate-600">{template.targetRoles.join(', ')}</p>
                <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold">
                  <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-700">{template.durationMinutes} min</span>
                  <span className="rounded-full bg-emerald-100 px-2 py-1 text-emerald-800">Pass {template.passMark}%</span>
                  <span className="rounded-full bg-amber-100 px-2 py-1 text-amber-800">Critical errors {template.maxCriticalErrorsAllowed}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {isLoading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-slate-600">Loading Recruitment & HR data...</div>
      ) : (
        <>
          <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-bold text-slate-950">Candidate pipeline</h2>
            <div className="mt-4 grid gap-3 md:grid-cols-3 xl:grid-cols-9">
              {stages.map((stage) => {
                const stageCandidates = candidates.filter((candidate) => candidate.pipelineStage === stage);
                return (
                  <button
                    type="button"
                    key={stage}
                    onClick={() => stageCandidates[0] && setSelectedId(stageCandidates[0].id)}
                    className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-left hover:bg-purple-50"
                  >
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{stage}</p>
                    <p className="mt-2 text-2xl font-bold text-slate-950">{stageCandidates.length}</p>
                  </button>
                );
              })}
            </div>
          </section>

          <div className="grid gap-6 xl:grid-cols-[390px_1fr]">
            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-xl font-bold text-slate-950">Candidates</h2>
              <div className="mt-4 max-h-[680px] space-y-3 overflow-y-auto">
                {candidates.map((candidate) => (
                  <button
                    key={candidate.id}
                    type="button"
                    onClick={() => setSelectedId(candidate.id)}
                    className={`w-full rounded-lg border p-4 text-left transition ${selectedCandidate?.id === candidate.id ? 'border-purple-300 bg-purple-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-bold text-slate-950">{candidate.fullName}</p>
                        <p className="mt-1 text-sm text-slate-600">{candidate.roleAppliedFor}</p>
                      </div>
                      <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">{candidate.pipelineStage}</span>
                    </div>
                    <p className="mt-3 text-xs text-slate-500">{candidate.email}</p>
                  </button>
                ))}
              </div>
            </section>

            {selectedCandidate && (
              <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-950">{selectedCandidate.fullName}</h2>
                    <p className="mt-1 text-sm text-slate-600">{selectedCandidate.roleAppliedFor} - {selectedCandidate.email} - {selectedCandidate.phone}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {[selectedCandidate.rightToWorkStatus, selectedCandidate.dbsStatus, selectedCandidate.referenceStatus, selectedCandidate.trainingStatus].map((status) => (
                        <span key={status} className={`rounded-full px-2 py-1 text-xs font-semibold ${statusClass(status)}`}>{status}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button type="button" onClick={() => void createAiSummary()} className="inline-flex items-center gap-2 rounded-md border border-purple-200 px-4 py-2 text-sm font-semibold text-purple-800 hover:bg-purple-50">
                      <Bot className="h-4 w-4" />
                      AI screening
                    </button>
                    <button type="button" onClick={() => toast.success('Reference email draft prepared for HR review')} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                      <Mail className="h-4 w-4" />
                      Reference email
                    </button>
                    <button type="button" onClick={() => toast.success('Contract draft ready in document workflow')} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                      <FileText className="h-4 w-4" />
                      Contract draft
                    </button>
                    <button type="button" onClick={() => void updateCandidate(selectedCandidate, { candidatePortalStatus: 'ACTIVE', candidatePortalLogin: null })} className="inline-flex items-center gap-2 rounded-md border border-purple-200 px-4 py-2 text-sm font-semibold text-purple-800 hover:bg-purple-50">
                      <KeyRound className="h-4 w-4" />
                      Issue exam login
                    </button>
                    <button type="button" onClick={() => void convertToCarer()} disabled={isSaving} className="inline-flex items-center gap-2 rounded-md bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60">
                      <UserPlus className="h-4 w-4" />
                      Convert to carer
                    </button>
                  </div>
                </div>

                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pipeline stage</span>
                    <select
                      value={selectedCandidate.pipelineStage}
                      onChange={(event) => void updateCandidate(selectedCandidate, { pipelineStage: event.target.value })}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    >
                      {stages.map((stage) => <option key={stage}>{stage}</option>)}
                    </select>
                  </label>
                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Availability</span>
                    <input
                      value={selectedCandidate.availability}
                      onChange={(event) => setCandidates((current) => current.map((candidate) => candidate.id === selectedCandidate.id ? { ...candidate, availability: event.target.value } : candidate))}
                      onBlur={(event) => void updateCandidate(selectedCandidate, { availability: event.target.value })}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    />
                  </label>
                </div>

                <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <h3 className="font-bold text-slate-950">AI screening summary</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-700">{selectedCandidate.aiScreeningSummary || 'No AI screening summary yet.'}</p>
                </div>

                <div className="mt-6 rounded-lg border border-purple-200 bg-purple-50/70 p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <h3 className="font-bold text-slate-950">Candidate exam portal login</h3>
                      <p className="mt-1 text-sm leading-6 text-slate-700">
                        Temporary login for the candidate-only AI exam page. After the candidate submits the exam, these credentials are removed automatically.
                      </p>
                    </div>
                    <span className={`w-fit rounded-full px-2 py-1 text-xs font-semibold ${statusClass(selectedCandidate.candidatePortalLogin?.status || selectedCandidate.candidatePortalStatus || 'MISSING')}`}>
                      {selectedCandidate.candidatePortalLogin?.status || selectedCandidate.candidatePortalStatus || 'MISSING'}
                    </span>
                  </div>
                  {selectedCandidate.candidatePortalLogin ? (
                    <div className="mt-3 grid gap-3 md:grid-cols-3">
                      <DetailPill label="Login URL" value="/candidate-exam" />
                      <DetailPill label="Email" value={selectedCandidate.candidatePortalLogin.email} />
                      <DetailPill label="Temporary password" value={selectedCandidate.candidatePortalLogin.temporaryPassword} />
                    </div>
                  ) : (
                    <p className="mt-3 rounded-md bg-white px-3 py-2 text-sm text-slate-600">No active candidate exam login. Use the button above to issue a fresh temporary login.</p>
                  )}
                  {(selectedCandidate.applicationSubmittedAt || selectedCandidate.candidateExamOutcome) && (
                    <div className="mt-4 grid gap-3 md:grid-cols-3">
                      <DetailPill label="Application submitted" value={selectedCandidate.applicationSubmittedAt ? new Date(selectedCandidate.applicationSubmittedAt).toLocaleString() : 'Not submitted'} />
                      <DetailPill label="Exam result" value={selectedCandidate.candidateExamOutcome || 'Pending'} />
                      <DetailPill label="Exam score" value={selectedCandidate.candidateExamScore || 'Pending'} />
                    </div>
                  )}
                  {selectedCandidate.candidatePortalMessage && (
                    <p className={`mt-3 rounded-md px-3 py-2 text-sm ${selectedCandidate.candidateExamOutcome === 'REJECTED' ? 'bg-red-50 text-red-800' : 'bg-emerald-50 text-emerald-800'}`}>
                      {selectedCandidate.candidatePortalMessage}
                    </p>
                  )}
                </div>

                <div className="mt-6 rounded-lg border border-purple-200 bg-purple-50/60 p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <h3 className="flex items-center gap-2 font-bold text-slate-950">
                        <Bot className="h-5 w-5 text-purple-700" />
                        AI CV screening
                      </h3>
                      <p className="mt-2 text-sm leading-6 text-slate-700">
                        Paste or upload CV text. AI extracts experience, skills, gaps, availability and red flags, but HR or the manager must make the final decision.
                      </p>
                    </div>
                    <span className="inline-flex w-fit items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-purple-800">
                      <ShieldCheck className="h-4 w-4" />
                      Human approval required
                    </span>
                  </div>

                  <div className="mt-4 grid gap-4 xl:grid-cols-[1fr_280px]">
                    <label className="space-y-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">CV text</span>
                      <textarea
                        value={cvText}
                        onChange={(event) => setCvText(event.target.value)}
                        rows={8}
                        placeholder="Paste the candidate CV text here..."
                        className="w-full rounded-md border border-purple-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-purple-500 focus:outline-none"
                      />
                    </label>
                    <div className="space-y-3">
                      <label className="block rounded-lg border border-dashed border-purple-300 bg-white p-4 text-center text-sm text-slate-700 hover:bg-purple-50">
                        <Upload className="mx-auto h-6 w-6 text-purple-700" />
                        <span className="mt-2 block font-semibold">Upload text CV</span>
                        <span className="mt-1 block text-xs text-slate-500">TXT, DOC/DOCX text, PDF text if readable</span>
                        <input
                          type="file"
                          accept=".txt,.md,.csv,.doc,.docx,.pdf"
                          className="hidden"
                          onChange={(event) => void handleCvFile(event.target.files?.[0] || null)}
                        />
                      </label>
                      {cvFileName && <p className="rounded-md bg-white px-3 py-2 text-xs font-medium text-slate-600">{cvFileName}</p>}
                      <button
                        type="button"
                        onClick={() => void runCvScreening()}
                        disabled={isScreeningCv}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60"
                      >
                        <Bot className="h-4 w-4" />
                        {isScreeningCv ? 'Screening CV...' : 'Run CV screening'}
                      </button>
                    </div>
                  </div>

                  {selectedCandidate.cvScreening && (
                    <div className="mt-5 grid gap-3 lg:grid-cols-2">
                      <ScreeningBox title="Experience" items={[selectedCandidate.cvScreening.experience]} />
                      <ScreeningBox title="Availability" items={selectedCandidate.cvScreening.availability} empty="No clear availability detected." />
                      <ScreeningBox title="Skills detected" items={selectedCandidate.cvScreening.skills} empty="No clear skills detected." />
                      <ScreeningBox title="Gaps to verify" items={selectedCandidate.cvScreening.gaps} empty="No major gaps detected." />
                      <ScreeningBox title="Red flags for HR review" items={selectedCandidate.cvScreening.redFlags} empty="No red flags detected from the supplied CV text." warning />
                      <div className="rounded-lg border border-slate-200 bg-white p-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Recommendation</p>
                        <p className="mt-2 text-lg font-bold text-slate-950">{selectedCandidate.cvScreening.recommendation}</p>
                        <p className="mt-2 text-sm text-slate-600">Confidence: {selectedCandidate.cvScreening.confidence}</p>
                        <p className="mt-2 text-xs font-semibold text-purple-800">{selectedCandidate.cvScreening.decisionOwner} must approve before any hiring decision.</p>
                      </div>
                    </div>
                  )}
                </div>

                <div id="interview-exam-workspace" className="mt-6 scroll-mt-24 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                    <div>
                      <h3 className="flex items-center gap-2 font-bold text-slate-950">
                        <ClipboardCheck className="h-5 w-5 text-purple-700" />
                        Interview automation and candidate exam
                      </h3>
                      <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600">
                        Structured employment exam with role-specific questions, UK safe recruitment rules, AI assessment, candidate answers, HR notes, score, final recommendation and PDF interview record.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={applyAiAssessment}
                        className="inline-flex items-center gap-2 rounded-md border border-purple-200 px-4 py-2 text-sm font-semibold text-purple-800 hover:bg-purple-50"
                      >
                        <Bot className="h-4 w-4" />
                        AI assess
                      </button>
                      <button
                        type="button"
                        onClick={() => void runAiAssessmentAndSave()}
                        disabled={isSavingInterview}
                        className="inline-flex items-center gap-2 rounded-md border border-emerald-200 px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-50 disabled:opacity-60"
                      >
                        <Sparkles className="h-4 w-4" />
                        AI assess and save
                      </button>
                      <button
                        type="button"
                        onClick={() => void saveInterviewExam()}
                        disabled={isSavingInterview}
                        className="inline-flex items-center gap-2 rounded-md bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60"
                      >
                        <Save className="h-4 w-4" />
                        Save exam
                      </button>
                      <button
                        type="button"
                        onClick={() => void downloadInterviewPdf()}
                        className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        <Download className="h-4 w-4" />
                        PDF record
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_260px_180px]">
                    <label className="space-y-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Exam template</span>
                      <select
                        value={selectedExamTemplateId}
                        onChange={(event) => setSelectedExamTemplateId(event.target.value)}
                        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                      >
                        {roleExamTemplates.map((template) => (
                          <option key={template.id} value={template.id}>{template.title}</option>
                        ))}
                      </select>
                    </label>
                    <div className="flex items-end gap-2">
                      <button
                        type="button"
                        onClick={loadSelectedExamTemplate}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        <PlayCircle className="h-4 w-4" />
                        Load exam
                      </button>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        <Clock3 className="h-4 w-4" />
                        Timer
                      </p>
                      <p className="mt-1 text-sm font-bold text-slate-950">{activeExamTemplate.durationMinutes} minutes</p>
                      <p className="text-xs text-slate-500">{examStartedAt ? `Started ${new Date(examStartedAt).toLocaleString()}` : 'Not started'}</p>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-3 lg:grid-cols-3">
                    <div className="rounded-lg border border-purple-100 bg-purple-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-purple-800">Active template</p>
                      <p className="mt-2 font-bold text-slate-950">{activeExamTemplate.title}</p>
                      <p className="mt-1 text-sm text-slate-700">Decision owner: {activeExamTemplate.finalDecisionOwner}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Rule result</p>
                      <p className={`mt-2 font-bold ${examCanProgress ? 'text-emerald-700' : 'text-amber-800'}`}>
                        {examCanProgress ? 'Pass threshold met' : 'Manager review required'}
                      </p>
                      <p className="mt-1 text-sm text-slate-600">Weak answers {examRuleStatus.wrongAnswers}/{activeExamTemplate.maxWrongAnswersAllowed}. Critical errors {examRuleStatus.criticalErrors}/{activeExamTemplate.maxCriticalErrorsAllowed}.</p>
                    </div>
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-amber-900">Legal checks covered</p>
                      <p className="mt-2 text-sm leading-6 text-amber-950">{activeExamTemplate.legalBasis.slice(0, 2).join(' ')}</p>
                    </div>
                  </div>

                  {aiExamNarrative && (
                    <div className="mt-4 rounded-lg border border-purple-200 bg-white p-4">
                      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-purple-800">
                        <Bot className="h-4 w-4" />
                        AI exam guidance and result
                      </p>
                      <p className="mt-2 text-sm leading-6 text-slate-700">{aiExamNarrative}</p>
                    </div>
                  )}

                  <div className="mt-4 grid gap-4 lg:grid-cols-4">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Score</p>
                      <p className="mt-2 text-2xl font-bold text-slate-950">{interviewScore}/{interviewMaxScore}</p>
                      <p className="text-sm text-slate-600">{interviewPercentage}%</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pass mark</p>
                      <p className="mt-2 text-2xl font-bold text-slate-950">{activeExamTemplate.passMark}%</p>
                      <p className="text-sm text-slate-600">Manager approval still required</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 lg:col-span-2">
                      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        <Award className="h-4 w-4 text-purple-700" />
                        Recommendation
                      </p>
                      <p className="mt-2 text-lg font-bold text-slate-950">{interviewRecommendation}</p>
                      <p className="mt-1 text-sm text-slate-600">This is a structured recommendation only. HR/Registered Manager makes the final hiring decision.</p>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <label className="space-y-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Interviewer</span>
                      <input
                        value={interviewerName}
                        onChange={(event) => setInterviewerName(event.target.value)}
                        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="space-y-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Candidate declaration</span>
                      <input
                        value={candidateDeclaration}
                        onChange={(event) => setCandidateDeclaration(event.target.value)}
                        placeholder="Example: I confirm these answers are my own and true to the best of my knowledge."
                        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                      />
                    </label>
                  </div>

                  <div className="mt-5 space-y-4">
                    {interviewQuestions.map((question, index) => (
                      <div key={question.id} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-xs font-semibold uppercase tracking-wide text-purple-800">{index + 1}. {question.category}</p>
                              {question.critical && <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">Critical</span>}
                              {question.legalArea && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">{question.legalArea}</span>}
                            </div>
                            <p className="mt-2 font-semibold leading-6 text-slate-950">{question.question}</p>
                            <p className="mt-2 text-xs leading-5 text-slate-500">Expected evidence: {question.expectedAnswer}</p>
                            {question.keywords?.length ? (
                              <p className="mt-2 text-xs leading-5 text-slate-500">AI scoring evidence: {question.keywords.join(', ')}</p>
                            ) : null}
                          </div>
                          <label className="w-32 space-y-1">
                            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Score / {question.maxScore}</span>
                            <input
                              type="number"
                              min={0}
                              max={question.maxScore}
                              value={question.score}
                              onChange={(event) => updateInterviewQuestion(question.id, { score: Math.max(0, Math.min(question.maxScore, Number(event.target.value || 0))) })}
                              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                            />
                          </label>
                        </div>
                        <div className="mt-4 grid gap-3 lg:grid-cols-2">
                          <label className="space-y-2">
                            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Candidate answer</span>
                            <textarea
                              value={question.candidateAnswer}
                              onChange={(event) => updateInterviewQuestion(question.id, { candidateAnswer: event.target.value })}
                              rows={3}
                              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                            />
                          </label>
                          <label className="space-y-2">
                            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">HR notes</span>
                            <textarea
                              value={question.notes}
                              onChange={(event) => updateInterviewQuestion(question.id, { notes: event.target.value })}
                              rows={3}
                              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                            />
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>

                  <label className="mt-5 block space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Overall interview notes and final HR recommendation</span>
                    <textarea
                      value={interviewNotes}
                      onChange={(event) => setInterviewNotes(event.target.value)}
                      rows={4}
                      placeholder="Record strengths, risks, training needs, conditions, and final human recommendation."
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    />
                  </label>
                </div>

                <div className="mt-6 grid gap-6 xl:grid-cols-2">
                  <Panel title="Reference checks">
                    {selectedReferences.map((reference) => (
                      <div key={reference.id} className="rounded-lg border border-slate-200 bg-white p-4">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                          <div>
                            <p className="font-semibold text-slate-950">{reference.refereeName || 'Referee'}</p>
                            <p className="text-xs text-slate-500">{reference.relationship} - {reference.refereeEmail}</p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button type="button" onClick={() => void sendReferenceRequest(reference)} className="rounded-md border border-purple-200 px-3 py-2 text-xs font-semibold text-purple-800 hover:bg-purple-50">
                              Send email
                            </button>
                            <select
                              value={reference.status}
                              onChange={(event) => void updateReference(reference, { status: event.target.value, receivedAt: ['REPLIED', 'APPROVED', 'CONCERN'].includes(event.target.value) ? new Date().toISOString() : reference.receivedAt })}
                              className="rounded-md border border-slate-300 px-2 py-2 text-xs font-semibold"
                            >
                              {referenceStatuses.map((status) => <option key={status}>{status}</option>)}
                            </select>
                          </div>
                        </div>
                        <textarea
                          value={reference.outcome || ''}
                          onChange={(event) => setReferences((current) => current.map((item) => item.id === reference.id ? { ...item, outcome: event.target.value } : item))}
                          onBlur={(event) => void updateReference(reference, { outcome: event.target.value })}
                          placeholder="Save replied reference details, approval notes or concern here..."
                          rows={3}
                          className="mt-3 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                        />
                        <p className="mt-2 text-xs text-slate-500">Sent: {reference.sentAt || 'not sent'} | Replied: {reference.receivedAt || 'not replied'}</p>
                      </div>
                    ))}
                  </Panel>

                  <Panel title="DBS tracker">
                    <div className="rounded-lg border border-slate-200 bg-white p-4">
                      <label className="space-y-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">DBS status</span>
                        <select value={selectedCandidate.dbsStatus} onChange={(event) => void updateDbs(event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
                          {dbsStatuses.map((status) => <option key={status}>{status}</option>)}
                        </select>
                      </label>
                      <label className="mt-3 block space-y-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">DBS expiry date</span>
                        <input
                          type="date"
                          defaultValue={selectedDocuments.find((documentItem) => /DBS/i.test(documentItem.documentType))?.expiryDate || ''}
                          onBlur={(event) => void updateDbs(selectedCandidate.dbsStatus, event.target.value)}
                          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                        />
                      </label>
                      <div className="mt-4 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
                        Alerts are raised when DBS is expired, not started, awaiting result or marked concern. Candidate conversion is blocked until DBS is clear.
                      </div>
                    </div>
                  </Panel>
                </div>

                <div className="mt-6 grid gap-6 xl:grid-cols-2">
                  <Panel title="Training onboarding">
                    {selectedTrainingItems.map((item) => (
                      <label key={item.label} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3">
                        <span>
                          <span className="block font-semibold text-slate-950">{item.label}</span>
                          <span className="text-xs text-slate-500">Mandatory before carer can become active</span>
                        </span>
                        <input
                          type="checkbox"
                          checked={item.completed}
                          onChange={(event) => {
                            const nextItems = selectedTrainingItems.map((trainingItem) => trainingItem.label === item.label ? { ...trainingItem, completed: event.target.checked } : trainingItem);
                            void saveTrainingChecklist(nextItems);
                          }}
                          className="h-5 w-5 accent-purple-700"
                        />
                      </label>
                    ))}
                  </Panel>

                  <Panel title="Contract generation and signature">
                    <div className="rounded-lg border border-slate-200 bg-white p-4">
                      <div className="grid gap-3 md:grid-cols-2">
                        <label className="space-y-2">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Start date</span>
                          <input type="date" value={contractStartDate} onChange={(event) => setContractStartDate(event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
                        </label>
                        <label className="space-y-2">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Hourly rate</span>
                          <input value={contractHourlyRate} onChange={(event) => setContractHourlyRate(event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
                        </label>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button type="button" onClick={() => void generateContract()} className="inline-flex items-center gap-2 rounded-md bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800">
                          <FileText className="h-4 w-4" />
                          Generate contract
                        </button>
                        <button type="button" onClick={() => void signContract()} disabled={!selectedContract || selectedContract.status === 'SIGNED'} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                          <ShieldCheck className="h-4 w-4" />
                          Digital sign
                        </button>
                      </div>
                      <p className="mt-3 text-sm text-slate-600">Latest contract: {selectedContract ? `${selectedContract.title} - ${selectedContract.status}` : 'No contract generated yet.'}</p>
                    </div>
                  </Panel>
                </div>

                <div className="mt-6 grid gap-6 xl:grid-cols-2">
                  <Panel title="Carer conversion controls">
                    <div className="rounded-lg border border-slate-200 bg-white p-4">
                      <p className="text-sm leading-6 text-slate-700">Conversion creates the active carer profile, branch allocation, availability, hourly rate, document folder and carer login. The backend blocks conversion if Right to Work, DBS, references, training or signed contract are incomplete.</p>
                      <button type="button" onClick={() => void convertToCarer()} disabled={isSaving || Boolean(selectedCandidate.convertedCarerId)} className="mt-4 inline-flex items-center gap-2 rounded-md bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60">
                        <UserPlus className="h-4 w-4" />
                        Convert approved candidate to carer
                      </button>
                      {lastCarerLogin && (
                        <div className="mt-4 rounded-md bg-emerald-50 p-3 text-sm text-emerald-900">
                          Carer login created: {lastCarerLogin.email} / {lastCarerLogin.temporaryPassword}
                        </div>
                      )}
                    </div>
                  </Panel>

                  <Panel title="Automated reminders and HR audit trail">
                    <div className="space-y-3">
                      {selectedReminders.slice(0, 5).map((reminder) => (
                        <div key={reminder.id} className="rounded-lg border border-slate-200 bg-white p-3">
                          <p className="font-semibold text-slate-950">{reminder.ruleTitle || reminder.message}</p>
                          <p className="text-xs text-slate-500">{reminder.status} - {reminder.priority || 'MEDIUM'}</p>
                        </div>
                      ))}
                      {selectedAuditEvents.map((event) => (
                        <div key={event.id} className="rounded-lg border border-slate-200 bg-white p-3">
                          <p className="font-semibold text-slate-950">{event.action}</p>
                          <p className="text-xs text-slate-500">{event.at} - {event.actor} - {event.collection}</p>
                        </div>
                      ))}
                    </div>
                  </Panel>
                </div>

                <div className="mt-6 grid gap-6 xl:grid-cols-2">
                  <Panel title="Document checklist">
                    {selectedDocuments.map((documentItem) => (
                      <div key={documentItem.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3">
                        <div>
                          <p className="font-semibold text-slate-950">{documentItem.documentType}</p>
                          <p className="text-xs text-slate-500">{documentItem.notes || documentItem.expiryDate || 'No expiry'}</p>
                        </div>
                        <span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusClass(documentItem.status)}`}>{documentItem.status}</span>
                      </div>
                    ))}
                  </Panel>

                  <Panel title="Onboarding checklist">
                    {(selectedChecklist?.items || []).map((item) => (
                      <div key={item.label} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3">
                        <CheckCircle2 className={`h-4 w-4 ${item.completed ? 'text-emerald-600' : 'text-slate-300'}`} />
                        <span className="text-sm font-medium text-slate-800">{item.label}</span>
                      </div>
                    ))}
                  </Panel>
                </div>

                <div className="mt-6">
                  <Panel title="HR tasks">
                    {selectedTasks.map((task) => (
                      <div key={task.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3">
                        <div>
                          <p className="font-semibold text-slate-950">{task.title}</p>
                          <p className="text-xs text-slate-500">{task.owner} - due {task.dueDate}</p>
                        </div>
                        <span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusClass(task.status)}`}>{task.status}</span>
                      </div>
                    ))}
                  </Panel>
                </div>
              </section>
            )}
          </div>
        </>
      )}
    </div>
  );
};

function MetricCard({ label, value, icon }: { label: string; value: React.ReactNode; icon: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="rounded-lg bg-purple-50 p-2 text-purple-700 w-fit">{icon}</div>
      <p className="mt-4 text-2xl font-bold text-slate-950">{value}</p>
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  );
}

function ComplianceTile({ label, value, alert = false }: { label: string; value: React.ReactNode; alert?: boolean }) {
  return (
    <div className={`rounded-lg border p-4 ${alert ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}>
      <p className={`text-2xl font-bold ${alert ? 'text-amber-900' : 'text-slate-950'}`}>{value}</p>
      <p className={`mt-1 text-xs font-semibold uppercase tracking-wide ${alert ? 'text-amber-800' : 'text-slate-500'}`}>{label}</p>
    </div>
  );
}

function DetailPill({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-purple-100 bg-white p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 break-words text-sm font-semibold text-slate-950">{value}</p>
    </div>
  );
}

function ScreeningBox({ title, items, empty, warning = false }: { title: string; items: string[]; empty?: string; warning?: boolean }) {
  const visibleItems = items.filter(Boolean);
  return (
    <div className={`rounded-lg border bg-white p-4 ${warning ? 'border-amber-200' : 'border-slate-200'}`}>
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {warning && <AlertTriangle className="h-4 w-4 text-amber-600" />}
        {title}
      </p>
      <div className="mt-3 space-y-2">
        {visibleItems.length ? visibleItems.map((item) => (
          <p key={item} className="rounded-md bg-slate-50 px-3 py-2 text-sm leading-5 text-slate-700">{item}</p>
        )) : (
          <p className="text-sm text-slate-500">{empty || 'No data detected.'}</p>
        )}
      </div>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <h3 className="font-bold text-slate-950">{title}</h3>
      <div className="mt-3 space-y-2">{children}</div>
    </div>
  );
}
